(() => {
  if (window.SnackSharkNativeConfigurator) {
    window.SnackSharkNativeConfigurator.mount(document);
    return;
  }
  const instances = new Map();

  const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[char]);
  const safeUrl = value => {
    const raw = String(value || '').trim();
    if (!raw || /[\u0000-\u001f\u007f\\]/.test(raw)) return '';
    if (!/^(https?:\/\/|\/|#)/i.test(raw)) return '';
    try {
      const url = new URL(raw, window.location.origin);
      return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? (raw.startsWith('//') ? url.href : raw) : '';
    } catch (error) { return ''; }
  };
  const cleanHtml = html => {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    doc.querySelectorAll('script,style,iframe,object,embed,form,input,button,link,meta,svg,math').forEach(node => node.remove());
    doc.querySelectorAll('*').forEach(node => Array.from(node.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase();
      if (!['href', 'src', 'alt', 'title', 'colspan', 'rowspan', 'width', 'height'].includes(name)
        || (['href', 'src'].includes(name) && !safeUrl(attribute.value))) node.removeAttribute(attribute.name);
    }));
    doc.querySelectorAll('a[href]').forEach(node => node.setAttribute('rel', 'noopener noreferrer'));
    return doc.body.innerHTML;
  };
  const productImage = product => {
    const featured = product?.featured_image;
    const first = product?.images?.[0];
    return safeUrl((typeof featured === 'string' ? featured : featured?.src || featured?.url)
      || (typeof first === 'string' ? first : first?.src || first?.url) || '');
  };
  const productTags = product => Array.isArray(product?.tags)
    ? product.tags
    : String(product?.tags || '').split(',').map(tag => tag.trim()).filter(Boolean);
  const searchableProduct = product => `${product?.title || ''} ${productTags(product).join(' ')} ${product?._information?.flavour_names_de || ''} ${product?._information?.texture_de || ''}`.toLowerCase();
  const normalizeKey = value => String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const parseJson = (value, fallback) => {
    if (value && typeof value === 'object') return value;
    try { return JSON.parse(String(value || '')); }
    catch (error) { return fallback; }
  };
  const dietaryFields = [
    ['vegan_status', 'vegan', 'Vegan'], ['halal_status', 'halal', 'Halal'],
    ['vegetarian_status', 'vegetarisch', 'Vegetarisch'], ['gelatin_free_status', 'gelatinefrei', 'Gelatinefrei'],
    ['gluten_free_status', 'glutenfrei', 'Glutenfrei'], ['lactose_free_status', 'laktosefrei', 'Laktosefrei'],
    ['sugar_free_status', 'zuckerfrei', 'Zuckerfrei']
  ];
  const dietaryTag = /vegan|halal|vegetari|gelatin|gelatine|gluten|lacto|lakto|zuckerfrei|sugar.?free|ohne.?zucker/i;
  const informationFor = item => {
    const raw = item?.candy_information;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    return Object.fromEntries(Object.entries(raw).filter(([, value]) => typeof value === 'string'));
  };
  const effectiveTags = (tags, item) => {
    const strings = tags.filter(tag => typeof tag === 'string' && tag.trim());
    if (item?.role !== 'candy' && !item?.candy_information) return [...new Set(strings)];
    const information = informationFor(item);
    return [...new Set([...strings.filter(tag => !dietaryTag.test(tag)),
      ...dietaryFields.filter(([field]) => information[field] === 'ja').map(([, tag]) => tag)])];
  };
  const dietaryFieldFor = term => {
    const normalized = normalizeKey(term);
    return dietaryFields.find(([, tag]) => normalized === tag)?.[0]
      || (normalized === 'zuckerfrei ohne zucker' ? 'sugar_free_status' : null);
  };
  const matchesTerm = (product, term) => {
    const field = dietaryFieldFor(term);
    if (field && product?._erp?.role === 'candy') return product._information[field] === 'ja';
    return searchableProduct(product).includes(term.toLowerCase());
  };
  const catalogProduct = (item, studioRules = null) => {
    const flavors = parseJson(item?.flavor_profile_json, {});
    const dietary = parseJson(item?.dietary_tags_json, []);
    const flavorTags = Array.isArray(flavors)
      ? flavors
      : Object.entries(flavors || {}).filter(([, enabled]) => Boolean(enabled)).map(([tag]) => tag);
    const tags = [...new Set([...(Array.isArray(dietary) ? dietary : []), ...flavorTags])].filter(Boolean);
    const portion = studioRules ? Number(studioRules.portion) : Number(item?.portion_grams) > 0 ? Number(item.portion_grams) : 1;
    const buffer = studioRules ? Math.max(0, Number(studioRules.buffer) || 0) : 0;
    const available = Number(item?.active) === 1 && portion > 0 && Number(item?.available_quantity || 0) >= portion + buffer;
    return {
      id: item.sku || item.title,
      title: item.title,
      featured_image: item.image_url || '',
      images: item.image_url ? [item.image_url] : [],
      tags: effectiveTags(tags, item),
      body_html: '',
      variants: [{ id: item.sku || item.title, sku: item.sku || '', available }],
      _erp: item,
      _information: informationFor(item)
    };
  };
  const mergeCatalogProduct = (item, shopifyProduct, studioRules = null) => {
    const product = catalogProduct(item, studioRules);
    if (!shopifyProduct) return product;
    product.tags = effectiveTags([...product.tags, ...productTags(shopifyProduct)], item);
    product.body_html = shopifyProduct.body_html || '';
    if (!product.featured_image) {
      product.featured_image = productImage(shopifyProduct);
      product.images = product.featured_image ? [product.featured_image] : [];
    }
    return product;
  };

  const mountRoot = root => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    let disposed = false;
    let mixCartTimer = null;
    let candyPreview = null;
    let candyZoomPreview = null, lastCandyPreviewOptions = null, studioDialogTrigger = null, studioUpgradeRevision = 0;
    let studioBarCleanup = null;
    let studioMobileCleanup = null;
    const on = (target, event, handler) => target?.addEventListener(event, handler, { signal: controller.signal });
    const localPath = path => {
      const prefix = (root.dataset.rootUrl || '/').replace(/\/$/, '');
      if (!String(path).startsWith('/') || (prefix && (path === prefix || path.startsWith(prefix + '/')))) return path;
      return prefix + path;
    };
    const request = (path, options = {}) => window.fetch(localPath(path), { ...options, signal: controller.signal });
    let copy = {}, translations = {};
    try { copy = JSON.parse(root.dataset.nativeCopy || '{}'); } catch (error) {}
    try { translations = JSON.parse(root.dataset.nativeI18n || '{}'); } catch (error) {}
    const text = (key, fallback) => typeof copy[key] === 'string' && copy[key].trim() ? copy[key]
      : typeof translations[key] === 'string' && translations[key].trim() ? translations[key] : fallback;
    const format = (key, fallback, values = {}) => text(key, fallback).replace(/\{(\w+)\}/g, (match, name) => String(values[name] ?? match));
    const label = (key, fallback) => escapeHtml(text(key, fallback));
    const nutrientFields = [
      ['energy_kj', 'energy', 'Brennwert', 'kJ'], ['energy_kcal', 'energy', 'Brennwert', 'kcal'],
      ['fat_g', 'fat', 'Fett', 'g'], ['saturates_g', 'saturates', 'davon gesättigte Fettsäuren', 'g'],
      ['carbohydrates_g', 'carbohydrates', 'Kohlenhydrate', 'g'], ['sugars_g', 'sugars', 'davon Zucker', 'g'],
      ['protein_g', 'protein', 'Eiweiß', 'g'], ['salt_g', 'salt', 'Salz', 'g'], ['fibre_g', 'fibre', 'Ballaststoffe', 'g']
    ];
    const renderCandyInformation = product => {
      const info = product._information || {};
      const value = field => typeof info[field] === 'string' && info[field].trim() ? info[field].trim() : '';
      const list = field => value(field).split('|').map(part => part.trim()).filter(Boolean).join(', ');
      const fact = (key, fallback, content) => `<div><dt>${label(key, fallback)}</dt><dd>${escapeHtml(content)}</dd></div>`;
      const statusText = status => status === 'ja' ? text('status_yes', 'Ja, laut erfasster Angabe')
        : status === 'nein' ? text('status_no', 'Nein') : text('status_unknown', 'Nicht bestätigt');
      const nutrient = field => /^(?:<|>|<=|>=|≤|≥)?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value(field)) ? value(field).replace('.', ',') : '';
      const missing = nutrientFields.slice(0, 8).some(([field]) => !nutrient(field));
      const allergenNames = ['Gluten', 'Krebstiere', 'Eier', 'Fisch', 'Erdnüsse', 'Soja', 'Milch', 'Schalenfrüchte', 'Sellerie', 'Senf', 'Sesam', 'Sulfite', 'Lupinen', 'Weichtiere'];
      const allergenStatus = parseJson(value('allergen_status_json'), {});
      const allergenLabels = { enthalten: text('allergen_contains', 'Enthalten'), spuren: text('allergen_traces', 'Mögliche Spuren'), abwesend: text('allergen_absent', 'Laut Angabe nicht enthalten') };
      const allergens = allergenStatus && typeof allergenStatus === 'object' && !Array.isArray(allergenStatus) && Object.keys(allergenStatus).length
        ? `<details class="native-info__allergens"><summary>${label('allergen_details', 'Alle Allergengruppen ansehen')}</summary><dl class="native-info__facts">${allergenNames.map((name, index) => fact(`allergen_${index}`, name, allergenLabels[allergenStatus[name]] || text('status_unknown', 'Nicht bestätigt'))).join('')}</dl></details>` : '';
      return `<div class="native-info__structured">
        ${!Object.keys(info).length ? `<p class="native-info__note">${label('information_missing', 'Für diese Sorte sind noch keine ausführlichen Zutaten- und Nährwertangaben hinterlegt.')}</p>` : ''}
        <h3>${label('ingredients_title', 'Zutaten')}</h3><p class="native-info__text">${escapeHtml(value('ingredients_de') || text('not_specified', 'Nicht angegeben'))}</p>
        <dl class="native-info__facts">${fact('allergens_title', 'Enthaltene Allergene', list('allergens_contains_de') || text('allergens_missing', 'Keine Angabe in der Allergenliste – nicht gleichbedeutend mit allergenfrei.'))}${fact('traces_title', 'Mögliche Spuren', list('allergens_may_contain_de') || text('traces_missing', 'Keine Spurenangabe erfasst'))}${fact('flavour_title', 'Geschmack', list('flavour_names_de') || text('status_unknown', 'Nicht bestätigt'))}${fact('texture_title', 'Konsistenz', value('texture_de') || text('status_unknown', 'Nicht bestätigt'))}</dl>${allergens}
        ${missing ? `<p class="native-info__note">${label('nutrition_incomplete', 'Nährwerte unvollständig. Fehlende Angaben sind nicht null.')}</p>` : ''}
        <table class="native-info__nutrition"><caption>${label('nutrition_title', 'Nährwertangaben')} ${value('nutrition_basis') === '100g' ? label('nutrition_basis', 'je 100 g') : label('nutrition_basis_missing', '– Bezugsmenge nicht angegeben')}</caption><thead><tr><th scope="col">${label('nutrient_heading', 'Nährwert')}</th><th scope="col">${label('amount_heading', 'Menge')}</th></tr></thead><tbody>${nutrientFields.map(([field, key, title, unit]) => `<tr><th scope="row">${label(key, title)}</th><td>${nutrient(field) ? `${escapeHtml(nutrient(field))} ${unit}` : label('not_specified', 'Nicht angegeben')}</td></tr>`).join('')}</tbody></table>
        <h3>${label('dietary_title', 'Ernährungseigenschaften')}</h3><dl class="native-info__facts native-info__properties">${dietaryFields.map(([field, tag, title]) => fact(`dietary_${tag}`, title, statusText(value(field)))).join('')}</dl>
        <p class="native-info__note">${label('dietary_note', 'Unbestätigte Eigenschaften sind keine positive Kennzeichnung. „Halal“ beschreibt die erfasste Angabe und bestätigt kein Zertifikat.')}</p>
        ${value('gelatin_source') ? `<dl class="native-info__facts">${fact('gelatin_title', 'Gelatineherkunft', value('gelatin_source'))}</dl>` : ''}
        ${value('warnings_de') ? `<div class="native-info__note"><h3>${label('warnings_title', 'Warnhinweise')}</h3><p class="native-info__text">${escapeHtml(value('warnings_de'))}</p></div>` : ''}
        ${value('storage_de') ? `<h3>${label('storage_title', 'Lagerung')}</h3><p class="native-info__text">${escapeHtml(value('storage_de'))}</p>` : ''}
      </div>`;
    };
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency', currency: root.dataset.currency || window.Shark?.currency || 'EUR'
    }).format((Number(cents) || 0) / 100);
    const loadCollection = async (handle, fresh = false) => {
      if (!handle) return [];
      const all = [];
      for (let page = 1; page <= 4; page += 1) {
        const response = await request(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, {
          ...(fresh ? { cache: 'no-store' } : {}),
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(text('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.'));
        const products = (await response.json()).products || [];
        all.push(...products);
        if (products.length < 250) break;
      }
      return all;
    };
    instances.set(root, () => {
      disposed = true;
      if(mixCartTimer)clearTimeout(mixCartTimer);
      controller.abort();
      candyPreview?.destroy();
      candyPreview = null;
      candyZoomPreview?.destroy(); candyZoomPreview = null;
      studioDialogs().forEach(dialog => { if (dialog.open) dialog.close(); });
      studioBarCleanup?.();
      studioMobileCleanup?.();
      instances.delete(root);
      root.classList.remove('is-running');
      root.querySelectorAll('.native-config__drawer, [data-native-exit-confirm]').forEach(dialog => { dialog.hidden = true; dialog.setAttribute('aria-hidden', 'true'); });
      document.body.classList.toggle('native-config-flow-active', !!document.querySelector('.native-config.is-running'));
      document.body.classList.toggle('native-config-dialog-active', !!document.querySelector('.native-config__drawer:not([hidden])'));
    });
    const q = selector => root.querySelector(selector);
    const qa = selector => Array.from(root.querySelectorAll(selector));
    const mode = root.dataset.mode || 'candy';
    const studio = mode === 'candy' && root.classList.contains('native-config--studio');
    // Standard Shopify basket again: ERP reads the mix from line properties
    // when the order arrives. No separate draft checkout or cart-time hold.
    const normalShopifyCart = studio && root.dataset.shopifyCartCheckout === 'true';
    const studioDialogs = () => [q('[data-studio-zoom]'), q('[data-studio-upgrade]'), q('[data-studio-random-dialog]')].filter(Boolean);
    const studioCheckoutEnabled = studio && root.dataset.studioCheckoutEnabled === 'true';
    if (studio) {
      const property = '--candy-studio-preview-inset';
      const previous = root.style.getPropertyValue(property);
      let observedWrapper = null, scheduled = false, frameId = null, lastInset = null;
      const measure = () => {
        if (disposed) return;
        const wrapper = document.getElementById('PBarNextFrameWrapper');
        if (wrapper !== observedWrapper) {
          resizeObserver?.disconnect(); observedWrapper = wrapper;
          if (wrapper) resizeObserver?.observe(wrapper);
        }
        let inset = 0;
        if (wrapper && !wrapper.hidden && wrapper.getAttribute('aria-hidden') !== 'true') {
          const rect = wrapper.getBoundingClientRect();
          const styles = window.getComputedStyle(wrapper);
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
          const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
          if (styles.display !== 'none' && styles.visibility !== 'hidden' && Number(styles.opacity) !== 0
            && rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < viewportWidth && rect.bottom >= viewportHeight - 1 && rect.top < viewportHeight) {
            inset = Math.ceil(Math.min(rect.height, viewportHeight - Math.max(0, rect.top)));
          }
        }
        if (lastInset !== inset) { lastInset = inset; root.style.setProperty(property, `${inset}px`); }
      };
      const schedule = () => {
        if (scheduled || disposed) return;
        scheduled = true;
        frameId = requestAnimationFrame(() => { scheduled = false; frameId = null; measure(); });
      };
      const resizeObserver = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(schedule) : null;
      const mutationObserver = typeof window.MutationObserver === 'function' ? new window.MutationObserver(schedule) : null;
      mutationObserver?.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
      if (typeof window.addEventListener === 'function') on(window, 'resize', schedule);
      if (window.visualViewport?.addEventListener) on(window.visualViewport, 'resize', schedule);
      studioBarCleanup = () => {
        resizeObserver?.disconnect(); mutationObserver?.disconnect();
        if (frameId !== null && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frameId);
        if (previous) root.style.setProperty(property, previous); else root.style.removeProperty(property);
      };
      measure();
    }
    const parsedPreviewConfig = studio ? parseJson(q('script[data-candy-preview-config]')?.textContent, {}) : {};
    const previewConfig = parsedPreviewConfig && typeof parsedPreviewConfig === 'object' && !Array.isArray(parsedPreviewConfig) ? parsedPreviewConfig : {};
    const imageOverridesFor = rows => {
      const overrides = new Map();
      (Array.isArray(rows) ? rows : []).forEach(item => {
        if (!item || typeof item.sku !== 'string' || !item.sku) return;
        const raw = typeof item.image_url === 'string' ? item.image_url.trim() : '';
        const url = raw && !/[\u0000-\u0020\u007f\\]/.test(raw) && /^(https?:\/\/|\/)/i.test(raw) ? safeUrl(raw) : '';
        // Duplicates within either source are ambiguous. An explicit invalid
        // editor row also disables the bundled override, rather than hiding it.
        overrides.set(item.sku, overrides.has(item.sku) ? null : url || null);
      });
      return overrides;
    };
    const candyImageOverrides = new Map([...imageOverridesFor(previewConfig.bundledCandyImages), ...imageOverridesFor(previewConfig.candyImages)]);
    const packageGroup = descriptor => descriptor.packaging || ({ bottle: 'flasche', bag: 'tuete', heart: 'sonderform', cup: 'sonderform', number: 'buchstaben_zahlen', letter: 'buchstaben_zahlen' })[descriptor.shape] || 'sonderform';
    const demoPackages = (Array.isArray(previewConfig.demoPackages) ? previewConfig.demoPackages : []).filter(item => item && typeof item.id === 'string' && item.id && typeof item.title === 'string' && Number.isInteger(Number(item.capacity)) && Number(item.capacity) > 0 && Number(item.capacity) <= 100);
    const studioBaseItems = new WeakMap();
    let studioVariantCatalog = null;
    const pendingKey = `${normalShopifyCart?'snacksharkShopifyMix':'snacksharkPendingMix'}:${mode}:${root.dataset.sectionId || 'configurator'}`;
    const readPending = () => { try { return studio ? parseJson(sessionStorage.getItem(pendingKey), null) : null; } catch (error) { return null; } };
    const mixBasketKey = `${pendingKey}:basket`;
    const readMixBasket = () => { try { const rows=parseJson(sessionStorage.getItem(mixBasketKey),[]);return Array.isArray(rows)?rows.filter(row=>row?.request?.id):[]; } catch { return []; } };
    const writeMixBasket = rows => { if(rows.length)sessionStorage.setItem(mixBasketKey,JSON.stringify(rows));else sessionStorage.removeItem(mixBasketKey); };
    const initialPending = readPending();
    const steps = mode === 'muesli'
      ? ['packaging', 'size', 'ingredients', 'toppings', 'review']
      : ['packaging', 'size', 'ingredients', 'review'];
    const state = {
      step: 'packaging', packaging: '', base: null, ingredients: new Map(), toppings: new Map(),
      products: [], toppingProducts: [], catalog: null, catalogPromise: null, catalogError: '',
      advisorTerms: [], upsellsLoaded: false, lastAdvisorQuery: '', flowRevision: 0, submitting: false, inventoryReady: false,
      browseCatalog: null, browsePromise: null, demoMode: false, studioFilter: '', studioLoading: false, submitLocked: !!initialPending?.nonce,
      pendingNonce: initialPending?.nonce || '', pendingConfirmed: false,
      studioView: 'creation', studioTargetSlot: null, studioSlots: []
    };
    // Clearing only after leaving the page preserves the guard if navigation is
    // blocked, throws, or the section is remounted in the same page.
    if (studio && typeof window.addEventListener === 'function') on(window, 'pagehide', () => {
      if (!state.pendingConfirmed || !state.pendingNonce) return;
      try { if (readPending()?.nonce === state.pendingNonce) sessionStorage.removeItem(pendingKey); } catch (error) {}
    });
    const affinityKey = 'snacksharkMixAffinity';
    const readAffinity = () => {
      try { return JSON.parse(sessionStorage.getItem(affinityKey) || '{}'); }
      catch (error) { return {}; }
    };
    const recordAffinity = (query, productId) => {
      if (!query || !productId) return;
      const affinity = readAffinity();
      const key = query.toLowerCase().trim();
      affinity[key] = affinity[key] || {};
      affinity[key][String(productId)] = Number(affinity[key][String(productId)] || 0) + 1;
      try { sessionStorage.setItem(affinityKey, JSON.stringify(affinity)); } catch (error) {}
    };
    qa('button.native-pack-card[data-packaging]').forEach(button => { button.disabled = true; });
    const status = q('[data-native-status]');
    const mobileNext = studio ? q('[data-studio-mobile-next]') : null;
    const nextButtons = [...qa('[data-native-next], [data-native-next-top]'), ...(mobileNext ? [mobileNext] : [])];
    const back = q('[data-native-back]');
    const backButtons = [back, studio ? q('[data-studio-mobile-back]') : null].filter(Boolean);
    const footerLabel = q('[data-footer-label]');
    const footerTotal = q('[data-footer-total]');
    const studioMobileQuery = studio && typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;
    const studioDock = studio ? q('[data-selection-dock]') : null;
    const studioMobileSlot = studio ? q('[data-studio-mobile-selection]') : null;
    const studioMobileNav = studio ? q('[data-studio-mobile-nav]') : null;
    const studioCreationTools = studio ? q('[data-studio-creation-tools]') : null;
    const studioSlotTray = studio ? q('[data-studio-slot-tray]') : null;
    const studioSlots = studio ? q('[data-studio-slots]') : null;
    const studioPickerBar = studio ? q('[data-studio-picker-bar]') : null;
    const studioPreviewPanel = studio ? q('.candy-studio__preview') : null;
    const studioPreviewWasInert = !!studioPreviewPanel?.inert;
    const studioTwoViewMarkup = !!(studioCreationTools && studioSlotTray && studioSlots && studioPickerBar
      && q('[data-studio-slots-label]') && q('[data-studio-picker-summary]') && q('[data-studio-picker-close]')
      && q('[data-studio-clear]') && q('[data-studio-random]') && studioMobileNav && mobileNext && q('[data-studio-mobile-back]'));
    // Unusually large ERP capacities retain the existing quantity interface;
    // never silently truncate a package or allocate an unbounded slot array.
    const studioSlotCapacitySupported = () => Number.isSafeInteger(state.base?.pickCount) && state.base.pickCount > 0 && state.base.pickCount <= 200;
    const studioTwoViewActive = () => studioTwoViewMarkup && studioSlotCapacitySupported() && studioMobileQuery?.matches && state.step === 'ingredients';
    let studioSlotBase = null, studioSlotSignature = '', studioSlotIntent = null, studioPickerScroll = 0, studioCreationScroll = 0, studioViewRevision = 0, studioPickerFilterWasOpen = null;
    let studioDuplicateIntent = null, studioReplacementIntent = null, studioRandomIntent = null, studioRandomPreference = '';
    const restoreStudioPickerFilter = () => {
      const menu = q('[data-studio-filter-menu]');
      if (menu && studioPickerFilterWasOpen !== null) menu.open = studioPickerFilterWasOpen;
      studioPickerFilterWasOpen = null;
    };
    const studioViewportProperties = ['--candy-studio-viewport-height', '--candy-studio-viewport-top'];
    const studioViewportPrevious = studioTwoViewMarkup ? studioViewportProperties.map(name => root.style.getPropertyValue(name)) : [];
    const restoreStudioViewport = () => {
      if (!studioTwoViewMarkup) return;
      studioViewportProperties.forEach((name, index) => {
        if (studioViewportPrevious[index]) root.style.setProperty(name, studioViewportPrevious[index]);
        else root.style.removeProperty(name);
      });
    };
    const updateStudioViewport = () => {
      if (!studioTwoViewMarkup || disposed) return;
      if (!studioTwoViewActive()) { restoreStudioViewport(); return; }
      const viewport = window.visualViewport;
      const height = Number(viewport?.height || window.innerHeight), top = Number(viewport?.offsetTop || 0);
      if (Number.isFinite(height) && height > 0) root.style.setProperty(studioViewportProperties[0], `${height}px`);
      if (Number.isFinite(top) && top >= 0) root.style.setProperty(studioViewportProperties[1], `${top}px`);
    };
    if (studioTwoViewMarkup) {
      on(window, 'resize', updateStudioViewport);
      on(window.visualViewport, 'resize', updateStudioViewport);
      on(window.visualViewport, 'scroll', updateStudioViewport);
    }
    const studioDockAnchor = studioDock?.parentNode && document.createComment ? document.createComment('Candy selection home') : null;
    if (studioDockAnchor) studioDock.parentNode.insertBefore(studioDockAnchor, studioDock);
    let studioMobileSelectionActive = false;
    const syncStudioMobileLayout = () => {
      if (!studio) return;
      const twoView = studioTwoViewActive();
      const active = !!((!studioTwoViewMarkup || !studioSlotCapacitySupported()) && studioMobileQuery?.matches && state.step === 'ingredients' && studioDock && studioDockAnchor?.parentNode && studioMobileSlot && studioMobileNav && mobileNext && q('[data-studio-mobile-back]'));
      if (!twoView) { state.studioView = 'creation'; state.studioTargetSlot = null; studioSlotIntent = null; studioReplacementIntent = null; }
      if (twoView) root.dataset.studioMobileView = state.studioView;
      else delete root.dataset.studioMobileView;
      updateStudioViewport();
      const creation = twoView && state.studioView === 'creation';
      const desktopCreation = studioTwoViewMarkup && !studioMobileQuery?.matches && state.step === 'ingredients';
      const replaceBar = q('[data-studio-replace-bar]');
      if (replaceBar) replaceBar.hidden = !creation || !studioReplacementIntent;
      if (creation && studioReplacementIntent) root.dataset.studioReplaceTarget = 'true'; else delete root.dataset.studioReplaceTarget;
      if (!twoView || creation) restoreStudioPickerFilter();
      if (studioPreviewPanel && studioTwoViewMarkup) studioPreviewPanel.inert = studioPreviewWasInert || (twoView && !creation);
      if (studioCreationTools) studioCreationTools.hidden = !(creation || desktopCreation);
      if (studioSlotTray) studioSlotTray.hidden = !creation;
      if (studioPickerBar) studioPickerBar.hidden = !twoView || creation;
      const ingredientsScreen = qa('[data-screen]').find(screen => screen.dataset.screen === 'ingredients');
      if (studioTwoViewMarkup && ingredientsScreen) ingredientsScreen.hidden = state.step !== 'ingredients' || creation;
      const focus = document.activeElement;
      const movedFocus = !!focus && (studioDock?.contains(focus) || studioMobileNav?.contains(focus));
      if (active && studioDock.parentNode !== studioMobileSlot) studioMobileSlot.appendChild(studioDock);
      else if (!active && studioDockAnchor?.parentNode && studioDock.parentNode !== studioDockAnchor.parentNode) studioDockAnchor.parentNode.insertBefore(studioDock, studioDockAnchor.nextSibling);
      if (studioMobileSlot) studioMobileSlot.hidden = !active;
      if (studioMobileNav) studioMobileNav.hidden = !(active || creation || desktopCreation);
      updateStudioMeter();
      studioDock?.classList.toggle('is-studio-preview-selection', active);
      root.dataset.studioMobileSelection = String(active || twoView);
      studioMobileSelectionActive = active;
      if (movedFocus && !disposed) {
        if (twoView) { if (creation) focusStudioSlot(0); else q('[data-studio-picker-close]').focus({ preventScroll: true }); }
        else if (active || (['ingredients', 'toppings'].includes(state.step) && !studioMobileNav?.contains(focus))) focus.focus?.({ preventScroll: true });
        else {
          const heading = qa('[data-screen]').find(screen => screen.dataset.screen === state.step)?.querySelector('h2');
          if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
        }
      }
    };
    if (studio) studioMobileCleanup = () => {
      studioViewRevision += 1; studioSlotIntent = null; state.studioTargetSlot = null;
      studioDuplicateIntent = null; studioReplacementIntent = null; studioRandomIntent = null;
      restoreStudioPickerFilter();
      if (studioDockAnchor?.parentNode) studioDockAnchor.parentNode.insertBefore(studioDock, studioDockAnchor.nextSibling);
      studioDockAnchor?.remove();
      studioDock?.classList.remove('is-studio-preview-selection');
      if (studioMobileSlot) studioMobileSlot.hidden = true;
      if (studioMobileNav) studioMobileNav.hidden = true;
      [studioCreationTools, studioSlotTray, studioPickerBar, q('[data-studio-meter]')].forEach(node => { if (node) node.hidden = true; });
      if (studioPreviewPanel) studioPreviewPanel.inert = studioPreviewWasInert;
      restoreStudioViewport();
      delete root.dataset.studioMobileView;
      delete root.dataset.studioReplaceTarget;
      if (q('[data-studio-replace-bar]')) q('[data-studio-replace-bar]').hidden = true;
      root.dataset.studioMobileSelection = 'false';
    };

    const announce = message => {
      if (!status || disposed) return;
      status.textContent = '';
      requestAnimationFrame(() => { if (!disposed) status.textContent = message; });
    };
    const loadCatalog = async () => {
      if (state.catalog) return state.catalog;
      if (state.catalogPromise) return state.catalogPromise;
      const catalogUrl = root.dataset.catalogUrl;
      if (!catalogUrl) throw new Error(text('catalog_missing', 'Die Verbindung zum Mix-Sortiment fehlt.'));
      state.catalogPromise = request(catalogUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(async response => {
        if (disposed) throw new Error(text('configurator_closed', 'Konfigurator wurde geschlossen.'));
        if (!response.ok) throw new Error(text('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.'));
        const payload = await response.json();
        if (!['packaging', 'candy', 'cereal', 'topping'].every(group => Array.isArray(payload?.groups?.[group]))) {
          throw new Error(text('catalog_incomplete', 'Die Sortimentsdaten sind unvollständig. Bitte versuche es später erneut.'));
        }
        if (Array.isArray(payload.profiles)) payload.profiles = Object.fromEntries(payload.profiles.map(profile => [profile.profile_key, profile]));
        state.catalog = payload;
        state.catalogError = '';
        return payload;
      }).catch(error => {
        state.catalogError = text('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.');
        throw error;
      }).finally(() => { state.catalogPromise = null; });
      return state.catalogPromise;
    };

    // This read-only catalog is never substituted for the order-eligible catalog.
    const loadBrowseCatalog = async () => {
      if (state.browseCatalog) return state.browseCatalog;
      if (state.browsePromise) return state.browsePromise;
      const url = new URL(root.dataset.catalogUrl, window.location.origin);
      url.searchParams.set('include_unavailable', '1');
      state.browsePromise = request(url.href, { cache: 'no-store', headers: { Accept: 'application/json' } }).then(async response => {
        if (!response.ok) throw new Error(text('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.'));
        const payload = await response.json();
        if (disposed || !['packaging', 'candy', 'cereal', 'topping'].every(group => Array.isArray(payload?.groups?.[group]))) throw new Error(text('catalog_incomplete', 'Die Sortimentsdaten sind unvollständig. Bitte versuche es später erneut.'));
        if (Array.isArray(payload.profiles)) payload.profiles = Object.fromEntries(payload.profiles.map(profile => [profile.profile_key, profile]));
        state.browseCatalog = payload;
        return payload;
      }).finally(() => { state.browsePromise = null; });
      return state.browsePromise;
    };
    const demoReady = () => studio && state.demoMode && demoPackages.length > 0 && (state.browseCatalog?.groups?.candy || []).some(item => Number(item.active) === 1 && !!item.sku);
    const renderDemoPackages = () => {
      const grid = q('[data-native-base-products]');
      if (!studio || !grid) return;
      qa('[data-demo-wrap]').forEach(node => node.remove());
      if (!state.demoMode) return;
      demoPackages.forEach(descriptor => {
        const wrapper = document.createElement('div');
        wrapper.className = 'native-base-card-wrap';
        wrapper.dataset.demoWrap = descriptor.id;
        wrapper.dataset.packaging = packageGroup(descriptor);
        const image = safeUrl(descriptor.image_url || descriptor.front_image_url);
        wrapper.innerHTML = `<button type="button" class="native-base-card" data-demo-package="${escapeHtml(descriptor.id)}"><span class="native-base-card__media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : `<span>${label('studio_symbolic_package', 'Symbolische Verpackung')}</span>`}</span><span class="native-base-card__copy"><strong>${escapeHtml(descriptor.title)}</strong><span>${label('studio_preview_only', 'Vorschau – noch nicht bestellbar')}</span></span></button>`;
        const thumbnail = wrapper.querySelector('img');
        if (thumbnail && window.SnackSharkCandyPreview) {
          const visual = window.SnackSharkCandyPreview.createPlan({ package: descriptor }).package;
          thumbnail.style.clipPath = visual.imageClip || 'none';
          thumbnail.style.aspectRatio = String(visual.ratio || 1);
          if (visual.sticker) {
            const canvas = document.createElement('span');
            canvas.className = 'ss-package-thumbnail';
            canvas.style.aspectRatio = String(visual.ratio || 1);
            thumbnail.parentNode.insertBefore(canvas, thumbnail);
            canvas.appendChild(thumbnail);
            const sticker = document.createElement('span');
            sticker.className = 'ss-package-sticker';
            const bounds = visual.sticker.bounds;
            Object.assign(sticker.style, { left: `${bounds.x * 100}%`, top: `${bounds.y * 100}%`, width: `${bounds.width * 100}%`, height: `${bounds.height * 100}%` });
            sticker.innerHTML = `<img class="ss-package-sticker__logo" src="${escapeHtml(visual.sticker.logoUrl)}" alt=""><span class="ss-package-sticker__text">WUNSCHPRODUKT</span>`;
            canvas.appendChild(sticker);
          }
        }
        grid.appendChild(wrapper);
      });
    };
    const previewDescriptor = () => {
      if (state.base?._preview) return state.base._preview;
      if (!state.base && state.packaging) {
        const representative = demoPackages.find(descriptor => packageGroup(descriptor) === state.packaging);
        if (representative) return representative;
      }
      return { id: state.packaging || 'unselected', shape: ({ flasche: 'bottle', tuete: 'bag', sonderform: 'heart', buchstaben_zahlen: 'number' })[state.packaging] || 'bag' };
    };
    const updateStudioMeter = () => {
      if (!studio || disposed) return;
      const meter = q('[data-studio-meter]');
      if (meter) {
        const total = selectedTotal(state.ingredients);
        const limit = Number.isSafeInteger(state.base?.pickCount) && state.base.pickCount > 0 ? state.base.pickCount : 0;
        const caption = format('studio_portions', '{total} von {limit} Portionen', { total, limit });
        meter.hidden = state.step !== 'ingredients' || !limit || (studioTwoViewActive() && state.studioView === 'picker');
        const meterLabel = q('[data-studio-meter-label]'), value = q('[data-studio-meter-value]');
        const progress = q('[data-studio-meter-progress]'), meterCaption = q('[data-studio-meter-caption]');
        if (meterLabel) meterLabel.textContent = text('studio_mix_label', 'Dein Mix');
        if (value) value.textContent = `${total} / ${limit}`;
        if (meterCaption) meterCaption.textContent = caption;
        if (progress) {
          // HTML progress requires a positive maximum, even before a package
          // is chosen. The hidden no-package state is not a claimed capacity.
          progress.max = limit || 1; progress.value = total;
          progress.setAttribute('aria-valuetext', caption);
        }
      }
    };
    const updateCandyPreview = () => {
      if (!studio || disposed) return;
      const stage = q('[data-candy-preview]');
      const title = q('[data-studio-preview-title]');
      const summary = q('[data-studio-preview-summary]');
      if (title) title.textContent = state.base?.title || text('studio_preview_title', 'Dein Mix bleibt im Blick');
      if (summary) summary.textContent = state.base ? format('studio_portions', '{total} von {limit} Portionen', { total: selectedTotal(state.ingredients), limit: state.base.pickCount }) : state.packaging ? text('choose_size', 'Wähle deine Größe') : text('studio_choose_package', 'Wähle deine Verpackung');
      updateStudioMeter();
      const change = q('[data-studio-change-package]');
      if (change) change.disabled = !state.packaging || state.submitting;
      const zoom = q('[data-studio-zoom-open]');
      if (zoom) zoom.disabled = !state.packaging || state.submitting || !window.SnackSharkCandyPreview;
      const zoomSummary = q('[data-studio-zoom-summary]');
      if (zoomSummary) zoomSummary.textContent = [title?.textContent, summary?.textContent].filter(Boolean).join(' · ');
      if (!stage || !window.SnackSharkCandyPreview) return;
      const options = {
        package: previewDescriptor(), items: state.ingredients, capacity: state.base?.pickCount || 0,
        seed: state.base?.sku || state.base?._preview?.id || state.packaging || 'unselected',
        labels: {
          empty: text('studio_preview_empty', 'Noch keine Sorten gewählt'),
          preview: text('studio_preview_illustrative', 'Illustrative Mix-Vorschau'),
          productPhotos: text('studio_preview_photos', 'Illustration mit Produktfotos'),
          symbolic: text('studio_symbolic_package', 'Symbolische Verpackung'),
          missing: text('studio_preview_missing', 'Für einige Sorten fehlt ein Vorschaubild'),
          packageMissing: text('studio_preview_package_missing', 'Verpackungsbild nicht verfügbar'),
          maskLoading: text('studio_mask_loading', 'Füllvorschau lädt …'),
          maskMissing: text('studio_mask_missing', 'Füllvorschau nicht verfügbar')
        }
      };
      lastCandyPreviewOptions = options;
      if (candyPreview) candyPreview.update(options);
      else candyPreview = window.SnackSharkCandyPreview.mount(stage, options);
      if (candyZoomPreview) candyZoomPreview.update({ ...options, animateInitial: false });
    };

    const closeStudioDialog = (restoreFocus = true) => {
      if (!studio) return;
      studioUpgradeRevision += 1;
      studioDialogs().forEach(dialog => { if (dialog.open) dialog.close(); dialog.setAttribute('aria-busy', 'false'); });
      studioDuplicateIntent = null; studioRandomIntent = null;
      candyZoomPreview?.destroy(); candyZoomPreview = null;
      if (restoreFocus && !disposed) studioDialogTrigger?.focus?.({ preventScroll: true });
      studioDialogTrigger = null;
    };
    const openStudioDialog = (dialog, trigger) => {
      if (!studio || !dialog || typeof dialog.showModal !== 'function' || disposed || state.submitting) return false;
      closeStudioDialog(false);
      studioDialogTrigger = trigger || document.activeElement;
      dialog.showModal();
      dialog.querySelector('[data-studio-dialog-close]')?.focus({ preventScroll: true });
      return true;
    };
    const openStudioZoom = trigger => {
      if (!state.packaging || !window.SnackSharkCandyPreview) return;
      updateCandyPreview();
      const stage = q('[data-studio-zoom-preview]');
      if (stage && lastCandyPreviewOptions && openStudioDialog(q('[data-studio-zoom]'), trigger)) {
        candyZoomPreview = window.SnackSharkCandyPreview.mount(stage, { ...lastCandyPreviewOptions, animateInitial: false });
      }
    };
    if (studio) studioDialogs().forEach(dialog => {
      on(dialog, 'cancel', event => { event.preventDefault(); closeStudioDialog(); });
      on(dialog, 'click', event => { if (event.target === dialog) closeStudioDialog(); });
    });

    const hydrateStudioBaseVariants = async (catalog, obsolete = () => false) => {
      // Liquid supplies the first variant only. Read all variants returned by the
      // configured collection, never infer an ID from a title/SKU or use demo IDs.
      if (!studio || !studioCheckoutEnabled || !root.dataset.baseCollection) return;
      const grid = q('[data-native-base-products]');
      if (!grid) return;
      if (catalog?.checkout?.mode === 'erp-reserved') {
        // Draft products intentionally never appear in the public collection.
        // Identities and prices come from the ERP; purchase checks stay server-side.
        studioVariantCatalog = new Map();
        for (const item of catalog.groups.packaging) {
          if (!/^SS-CFG-(BAG-(500|1000)|HEART-14|JAR-(1000|2500|5000)|NUMBER-[0-9])$/.test(item.sku)
            || !/^[1-9]\d*$/.test(String(item.shopify_variant_id || '')) || !/^[1-9]\d*$/.test(String(item.shopify_product_id || ''))
            || !Number.isSafeInteger(item.configurator_price_minor) || item.configurator_price_minor <= 0) continue;
          const descriptor = (previewConfig.packages || []).find(p => p.sku === item.sku);
          if (!descriptor) continue;
          const packaging = packageGroup(descriptor), variantId = String(item.shopify_variant_id), productId = String(item.shopify_product_id);
          let card = qa('[data-base-product]').find(c => c.dataset.sku === item.sku);
          if (!card) {
            const wrapper = document.createElement('div'); wrapper.className = 'native-base-card-wrap'; wrapper.dataset.baseWrap = ''; wrapper.dataset.packaging = packaging;
            card = document.createElement('button'); card.type = 'button'; card.className = 'native-base-card'; card.dataset.baseProduct = '';
            wrapper.appendChild(card); grid.appendChild(wrapper);
          }
          studioVariantCatalog.set(item.sku, {variantId, productId});
          Object.assign(card.dataset, {packaging, variantId, productId, sku:item.sku, title:item.title, price:String(item.configurator_price_minor), shopifyAvailable:'true', pickCount:String(item.max_units), toppingCount:'0', erpCheckout:'true'});
          card.disabled = true;
          const image = safeUrl(descriptor.image_url || descriptor.front_image_url);
          card.innerHTML = `<span class="native-base-card__media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}</span><span class="native-base-card__copy"><small data-base-availability></small><strong>${escapeHtml(item.title)}</strong><em>${escapeHtml(money(item.configurator_price_minor))}</em><span data-base-rule></span></span>`;
        }
        return;
      }
      const products = await loadCollection(root.dataset.baseCollection, true);
      if (disposed || obsolete()) return;
      const exactId = value => typeof value === 'number' && !Number.isSafeInteger(value) ? '' : /^[1-9]\d*$/.test(String(value || '')) ? String(value) : '';
      const minorPrice = value => {
        // /collections/.../products.json returns decimal major-unit prices.
        const match = String(value ?? '').match(/^(\d+)(?:\.(\d{1,2}))?$/);
        if (!match) return null;
        const minor = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
        return Number.isSafeInteger(minor) && minor >= 0 ? minor : null;
      };
      const bySku = new Map();
      products.forEach(product => {
        (Array.isArray(product.variants) ? product.variants : []).forEach(variant => {
          if (typeof variant.sku !== 'string' || !variant.sku) return;
          const productId = exactId(product.id), variantId = exactId(variant.id), price = minorPrice(variant.price);
          const entry = productId && variantId && price !== null ? { product, variant, productId, variantId, price } : null;
          const prior = bySku.get(variant.sku);
          if (bySku.has(variant.sku) && (!prior || !entry || prior.variantId !== entry.variantId || prior.productId !== entry.productId)) bySku.set(variant.sku, null);
          else bySku.set(variant.sku, entry);
        });
      });
      studioVariantCatalog = bySku;
      const erpBySku = new Map();
      (catalog?.groups?.packaging || []).filter(item => item && typeof item.sku === 'string' && item.sku && item.mix_type === 'candy' && Number(item.active) === 1).forEach(item => {
        erpBySku.set(item.sku, erpBySku.has(item.sku) ? null : item);
      });
      const previews = Array.isArray(previewConfig.packages) ? previewConfig.packages : [];
      bySku.forEach((entry, sku) => {
        if (!entry || !erpBySku.get(sku)) return;
        const matching = qa('[data-base-product]').filter(card => String(card.dataset.variantId) === entry.variantId && String(card.dataset.productId) === entry.productId);
        let card = matching[0];
        const productCard = qa('[data-base-product]').find(candidate => String(candidate.dataset.productId) === entry.productId);
        const descriptor = previews.find(item => item?.sku === sku);
        const packaging = descriptor ? packageGroup(descriptor) : productCard?.dataset.packaging || 'sonderform';
        if (!card) {
          const wrapper = document.createElement('div');
          wrapper.className = 'native-base-card-wrap'; wrapper.dataset.baseWrap = ''; wrapper.dataset.packaging = packaging; wrapper.dataset.studioVariantWrap = entry.variantId;
          card = document.createElement('button'); card.type = 'button'; card.className = 'native-base-card';
          card.dataset.baseProduct = ''; card.dataset.studioVariant = 'true';
          wrapper.appendChild(card); grid.appendChild(wrapper);
        }
        const variantTitle = typeof entry.variant.title === 'string' && entry.variant.title !== 'Default Title' ? entry.variant.title : '';
        const title = [entry.product.title, variantTitle].filter(Boolean).join(' – ');
        Object.assign(card.dataset, { packaging, variantId: entry.variantId, productId: entry.productId, sku, title, price: String(entry.price), shopifyAvailable: String(entry.variant.available === true), pickCount: '0', toppingCount: '0' });
        const wrapper = card.closest('[data-base-wrap]');
        if (wrapper) wrapper.dataset.packaging = packaging;
        card.disabled = true;
        const image = safeUrl(descriptor?.image_url || descriptor?.front_image_url) || productImage({ ...entry.product, featured_image: entry.variant.featured_image || entry.product.featured_image });
        card.innerHTML = `<span class="native-base-card__media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : `<span>${label('studio_no_image', 'Noch kein Produktfoto')}</span>`}</span><span class="native-base-card__copy"><small data-base-availability></small><strong>${escapeHtml(title)}</strong><em>${escapeHtml(money(entry.price))}</em><span data-base-rule></span></span>`;
      });
    };

    const applyCatalogToBaseProducts = catalog => {
      const profile = catalog?.profiles?.[mode === 'muesli' ? 'cereal' : 'candy'] || {};
      const packaging = (catalog?.groups?.packaging || []).filter(item => {
        const itemMode = String(item.mix_type || '').toLowerCase();
        return studio ? itemMode === 'candy' : !itemMode || itemMode === (mode === 'muesli' ? 'cereal' : 'candy');
      });
      const skuKey = value => studio ? String(value || '') : String(value || '').trim().toLowerCase();
      const bySku = new Map();
      packaging.filter(item => item.sku).forEach(item => { const key = skuKey(item.sku); bySku.set(key, studio && bySku.has(key) ? null : item); });
      const byTitle = new Map();
      packaging.forEach(item => {
        const key = normalizeKey(item.title);
        byTitle.set(key, byTitle.has(key) ? null : item);
      });
      qa('[data-base-product]').forEach(card => {
        const shopifySku = skuKey(card.dataset.sku);
        const item = shopifySku ? bySku.get(shopifySku) : studio ? null : byTitle.get(normalizeKey(card.dataset.title));
        if (!item) {
          card.disabled = true;
          card.dataset.erpSku = '';
          card.dataset.erpAvailable = '0';
          card.classList.add('is-soldout');
          card.setAttribute('aria-disabled', 'true');
          return;
        }
        const pickCount = Number(studio ? item.max_units : item.max_units || profile.max_ingredients || card.dataset.pickCount || 0);
        const toppingCount = mode === 'muesli'
          ? Number(item.max_toppings ?? profile.max_toppings ?? card.dataset.toppingCount ?? 0)
          : 0;
        const variantMatch = !studioVariantCatalog || (studioVariantCatalog.get(shopifySku)?.variantId === String(card.dataset.variantId) && studioVariantCatalog.get(shopifySku)?.productId === String(card.dataset.productId));
        const reservedCheckout = studio && catalog?.checkout?.mode === 'erp-reserved';
        const portion = reservedCheckout ? Math.ceil(Number(item.fill_total_grams) / pickCount) + Number(item.reserve_per_slot_grams || 0) : Number(item.portion_grams);
        const available = card.dataset.shopifyAvailable === 'true' && Number(item.active) === 1 && Number(item.available_quantity || 0) >= 1
          && (!reservedCheckout || (catalog.checkout.enabled === true && card.dataset.erpCheckout === 'true'))
          && (!studio || (studioCheckoutEnabled && variantMatch && Number(profile.active) === 1 && portion > 0 && Number.isSafeInteger(portion) && Number.isSafeInteger(pickCount) && pickCount > 0 && /^\d+$/.test(String(card.dataset.variantId)) && Number(card.dataset.variantId) > 0));
        if (studio) { studioBaseItems.set(card, { item, profile }); card.dataset.portionGrams = String(portion || 0); }
        card.disabled = !available;
        card.dataset.pickCount = String(pickCount);
        card.dataset.toppingCount = String(toppingCount);
        card.dataset.erpSku = item.sku || '';
        card.dataset.erpAvailable = String(Number(item.available_quantity || 0));
        card.classList.toggle('is-soldout', !available);
        card.setAttribute('aria-disabled', available ? 'false' : 'true');
        const availability = card.querySelector('[data-base-availability]');
        const rule = card.querySelector('[data-base-rule]');
        if (availability) availability.textContent = available ? text('available', 'Verfügbar') : text('sold_out', 'Aktuell nicht verfügbar');
        if (rule) rule.textContent = format('pick_count', '{count} Portionen auswählbar', { count: pickCount }) + (toppingCount > 0 ? format('topping_count', ' + {count} Toppings', { count: toppingCount }) : '');
      });
      const ingredients = catalog?.groups?.[mode === 'muesli' ? 'cereal' : 'candy'] || [];
      const availableItem = item => Number(item.active) === 1 && Number(item.available_quantity || 0) >= (Number(item.portion_grams) > 0 ? Number(item.portion_grams) : 1);
      state.inventoryReady = studio
        ? qa('[data-base-product]').some(card => !card.disabled && ingredients.some(item => Number(item.active) === 1 && !!item.sku && Number(item.available_quantity || 0) >= Number(card.dataset.portionGrams) + Math.max(0, Number(profile.stock_buffer) || 0)))
        : packaging.some(availableItem) && ingredients.some(availableItem) && qa('[data-base-product]').some(card => !card.disabled && !card.classList.contains('is-soldout'));
      qa('button.native-pack-card[data-packaging]').forEach(button => { button.disabled = !state.inventoryReady && !demoReady(); });
      root.setAttribute('data-native-available', state.inventoryReady ? 'true' : 'false');
    };
    const drawerTriggers = new Map();
    const setDrawer = (drawer, open) => {
      if (!drawer) return;
      if (open && drawer.hidden) drawerTriggers.set(drawer, document.activeElement);
      drawer.hidden = !open;
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.classList.toggle('native-config-dialog-active', !!document.querySelector('.native-config__drawer:not([hidden])'));
      if (open) setTimeout(() => { if (!disposed) drawer.querySelector('aside')?.focus?.(); }, 30);
      else if (drawerTriggers.has(drawer)) { drawerTriggers.get(drawer)?.focus?.(); drawerTriggers.delete(drawer); }
    };
    const selectedTotal = map => Array.from(map.values()).reduce((sum, entry) => sum + entry.qty, 0);
    const selectedList = map => Array.from(map.values()).filter(entry => entry.qty > 0);
    const hasPortions = (product, count) => {
      if (studio) {
        if (state.studioLoading) return false;
        if (!product || Number(product._erp?.active) !== 1 || !product._erp?.sku || !Number.isInteger(count) || count < 1) return false;
        if (state.demoMode) return !!state.base?.demo;
        const portion = Number(state.base?.portionGrams);
        const profile = state.catalog?.profiles?.candy;
        return studioCheckoutEnabled && Number(profile?.active) === 1 && portion > 0 && Number.isSafeInteger(portion)
          && Number(product._erp.available_quantity || 0) >= count * portion + Math.max(0, Number(profile.stock_buffer) || 0);
      }
      const portion = Number(product?._erp?.portion_grams) > 0 ? Number(product._erp.portion_grams) : 1;
      return product?.variants?.some(variant => variant.available) && Number(product?._erp?.available_quantity || 0) >= count * portion;
    };
    const selectionText = map => selectedList(map).map(entry => `${entry.qty}× ${entry.product.title}`).join(', ');
    const baseCardFor = base => qa('[data-base-product]').find(card => String(card.dataset.variantId) === String(base?.variantId));
    const validBase = () => {
      if (studio && state.demoMode) return !!(demoReady() && state.base?.demo && state.base.packaging === state.packaging
        && demoPackages.some(item => item.id === state.base._preview?.id && Number(item.capacity) === state.base.pickCount));
      const card = baseCardFor(state.base);
      return !!(state.catalog && state.inventoryReady && state.base && card && !card.disabled && card.getAttribute('aria-disabled') !== 'true'
        && !card.classList.contains('is-soldout') && card.dataset.packaging === state.packaging
        && /^\d+$/.test(String(state.base.variantId)) && Number(state.base.variantId) > 0
        && Number.isInteger(Number(card.dataset.pickCount)) && Number(card.dataset.pickCount) > 0
        && !!card.dataset.erpSku && String(card.dataset.erpSku) === String(state.base.sku || '')
        && Number(card.dataset.pickCount) === state.base.pickCount
        && Number(card.dataset.toppingCount) === state.base.toppingCount
        && (!studio || (studioCheckoutEnabled && Number(state.catalog?.profiles?.candy?.active) === 1 && Number(card.dataset.portionGrams) > 0 && Number.isSafeInteger(Number(card.dataset.portionGrams)) && Number(card.dataset.portionGrams) === state.base.portionGrams && Number(card.dataset.price) === state.base.price)));
    };
    const validEntries = (map, group) => selectedList(map).every(entry => {
      const current = ((studio && state.demoMode ? state.browseCatalog : state.catalog)?.groups?.[group] || []).find(item => String(item.sku || (!studio && item.title) || '') === String(entry.product.id));
      if (studio) return Number.isInteger(entry.qty) && entry.qty > 0 && !!current?.sku && Number(current.active) === 1
        && (state.demoMode ? !!state.base?.demo : studioCheckoutEnabled && Number.isSafeInteger(state.base?.portionGrams) && state.base.portionGrams > 0 && Number(current.available_quantity || 0) >= entry.qty * state.base.portionGrams + Math.max(0, Number(state.catalog?.profiles?.candy?.stock_buffer) || 0));
      const portion = Number(current?.portion_grams) > 0 ? Number(current.portion_grams) : 1;
      return Number.isInteger(entry.qty) && entry.qty > 0 && current && Number(current.active) === 1 && Number(current.available_quantity || 0) >= entry.qty * portion;
    });

    // The cart continues to use the original quantity Map. This small visual
    // ledger preserves individual portion positions, including duplicate SKUs.
    const reconcileStudioSlots = () => {
      if (!studioTwoViewMarkup) return;
      const limit = studioSlotCapacitySupported() ? state.base.pickCount : 0;
      if (studioSlotBase !== state.base) {
        studioSlotBase = state.base; state.studioSlots = []; studioSlotIntent = null;
        studioReplacementIntent = null;
        state.studioTargetSlot = null; state.studioView = 'creation'; studioPickerScroll = 0; studioCreationScroll = 0;
      }
      const remaining = new Map(selectedList(state.ingredients).map(entry => [String(entry.product.id), entry.qty]));
      state.studioSlots = Array.from({ length: limit }, (_, index) => {
        const id = state.studioSlots[index];
        if (!id || !(remaining.get(id) > 0)) return null;
        remaining.set(id, remaining.get(id) - 1); return id;
      });
      remaining.forEach((qty, id) => {
        for (let count = 0; count < qty; count += 1) {
          const index = state.studioSlots.indexOf(null);
          if (index < 0) break;
          state.studioSlots[index] = id;
        }
      });
    };
    const focusStudioSlot = index => {
      const button = Array.from(studioSlots?.querySelectorAll('[data-studio-slot]') || []).find(node => Number(node.dataset.studioSlot) === index);
      if (button && !button.disabled) {
        button.focus({ preventScroll: true });
        // The slot grid grows downward; focusing a portion must not move the scene.
      }
      else if (studioSlotTray && !studioSlotTray.hidden) { studioSlotTray.setAttribute('tabindex', '-1'); studioSlotTray.focus({ preventScroll: true }); }
    };
    const renderStudioSlots = () => {
      if (!studioTwoViewMarkup) return;
      reconcileStudioSlots();
      const total = selectedTotal(state.ingredients), limit = state.studioSlots.length;
      const countLabel = format('studio_slots_label', 'Deine Candys · {total}/{limit}', { total, limit });
      q('[data-studio-slots-label]').textContent = countLabel;
      const chosen = studioSlotIntent && state.ingredients.get(studioSlotIntent.oldId)?.product;
      const slotLabel = studioSlotIntent
        ? chosen ? format('studio_slot_replace', 'Candy {number}: {title} austauschen', { number: studioSlotIntent.index + 1, title: chosen.title })
          : format('studio_slot_empty', 'Candy {number} auswählen', { number: studioSlotIntent.index + 1 })
        : '';
      q('[data-studio-picker-summary]').textContent = [countLabel, slotLabel].filter(Boolean).join(' · ');
      const thumb = q('[data-studio-picker-thumb]');
      if (thumb) {
        const descriptor = previewDescriptor(), image = safeUrl(descriptor.image_url || descriptor.front_image_url);
        thumb.hidden = !image;
        if (image) thumb.src = image; else thumb.removeAttribute('src');
      }
      q('[data-studio-picker-close]').textContent = text('studio_picker_close_label', 'Zur Kreation');
      const clear = q('[data-studio-clear]'), random = q('[data-studio-random]');
      (clear.querySelector('span') || clear).textContent = text('studio_clear_label', 'Leeren');
      (random.querySelector('span') || random).textContent = text('studio_random_label', 'Zufall');
      clear.disabled = !!studioReplacementIntent || state.studioLoading || state.submitting || !total;
      random.disabled = !!studioReplacementIntent || state.studioLoading || state.submitting || !validBase();
      const disabled = state.studioLoading || state.submitting || !validBase();
      const replacing = studioReplacementIntent && state.products.find(product => String(product.id) === studioReplacementIntent.productId);
      const replaceSummary = q('[data-studio-replace-summary]');
      if (replaceSummary && replacing) replaceSummary.textContent = format('studio_replace_choose', 'Wähle eine Portion, die durch {title} ersetzt werden soll.', { title: replacing.title });
      if (q('[data-studio-replace-cancel]')) q('[data-studio-replace-cancel]').textContent = text('studio_replace_cancel', 'Austausch abbrechen');
      const signature = JSON.stringify([state.studioSlots, disabled, replacing?.id || '', state.products.map(product => [product.id, product.title, product._erp?.preview_image_url || productImage(product)])]);
      if (signature === studioSlotSignature) return;
      studioSlotSignature = signature;
      const nodes = state.studioSlots.map((id, index) => {
        const product = id ? state.ingredients.get(id)?.product : null;
        const wrapper = document.createElement('div'); wrapper.className = 'candy-studio__slot';
        wrapper.classList.toggle('is-filled', !!product);
        wrapper.classList.toggle('is-replace-target', !!replacing && !!product);
        const button = document.createElement('button'); button.type = 'button'; button.className = 'candy-studio__slot-button';
        button.dataset.studioSlot = String(index); button.disabled = disabled;
        button.setAttribute('aria-label', product && replacing
          ? format('studio_slot_replace_with', 'Candy {number}: {title} durch {replacement} ersetzen', { number: index + 1, title: product.title, replacement: replacing.title }) : product
          ? format('studio_slot_replace', 'Candy {number}: {title} austauschen', { number: index + 1, title: product.title })
          : format('studio_slot_empty', 'Candy {number} auswählen', { number: index + 1 }));
        const image = product && (safeUrl(product._erp?.preview_image_url) || productImage(product));
        if (image) { const img = document.createElement('img'); img.className = 'candy-studio__slot-image'; img.src = image; img.alt = ''; button.appendChild(img); }
        const title = document.createElement('span'); title.className = product ? 'candy-studio__slot-title' : 'candy-studio__slot-plus';
        title.textContent = product ? product.title : '+'; button.appendChild(title); wrapper.appendChild(button);
        const actions = document.createElement('div'); actions.className = 'candy-studio__slot-actions'; wrapper.appendChild(actions);
        if (product) {
          const add = document.createElement('button'); add.type = 'button'; add.className = 'candy-studio__slot-add';
          add.dataset.studioSlotAdd = String(index); add.dataset.productId = id; add.textContent = '+'; add.disabled = disabled || !!replacing;
          add.setAttribute('aria-label', format('studio_slot_add', 'Noch 1 Portion {title}', { title: product.title }));
          if (total >= limit) add.setAttribute('aria-haspopup', 'dialog');
          const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'candy-studio__slot-remove';
          remove.dataset.studioSlotRemove = String(index); remove.disabled = disabled || !!replacing; remove.textContent = '−';
          remove.setAttribute('aria-label', format('studio_slot_remove', 'Candy {number}: {title} entfernen', { number: index + 1, title: product.title }));
          actions.appendChild(remove);
          actions.appendChild(add);
        }
        return wrapper;
      });
      studioSlots.replaceChildren(...nodes);
    };
    const returnStudioCreation = (restoreFocus = true) => {
      if (!studioTwoViewActive()) return;
      const index = studioSlotIntent?.index ?? state.studioTargetSlot ?? 0;
      const fromPicker = state.studioView === 'picker';
      if (fromPicker) studioPickerScroll = q('.native-config__workspace')?.scrollTop || 0;
      studioViewRevision += 1; state.studioView = 'creation'; state.studioTargetSlot = null; studioSlotIntent = null;
      syncStudioMobileLayout(); renderStudioSlots();
      if (fromPicker) q('.native-config__workspace')?.scrollTo?.({ top: studioCreationScroll, behavior: 'instant' });
      if (restoreFocus) focusStudioSlot(index);
    };
    const openStudioSlot = index => {
      if (!studioTwoViewActive() || state.studioLoading || !validBase() || !Number.isInteger(index) || index < 0 || index >= state.base.pickCount) return;
      reconcileStudioSlots();
      studioCreationScroll = q('.native-config__workspace')?.scrollTop || 0;
      studioSlotIntent = { index, oldId: state.studioSlots[index], base: state.base, revision: state.flowRevision };
      state.studioTargetSlot = index; state.studioView = 'picker';
      const filterMenu = q('[data-studio-filter-menu]');
      if (filterMenu) {
        if (studioPickerFilterWasOpen === null) studioPickerFilterWasOpen = !!filterMenu.open;
        filterMenu.open = true;
      }
      const revision = ++studioViewRevision;
      syncStudioMobileLayout(); updateMeter('ingredients');
      q('[data-studio-picker-close]').focus({ preventScroll: true });
      requestAnimationFrame(() => {
        if (!disposed && revision === studioViewRevision && studioTwoViewActive() && state.studioView === 'picker') q('.native-config__workspace')?.scrollTo?.({ top: studioPickerScroll, behavior: 'instant' });
      });
    };
    const studioSelectionForSlot = (product, intent) => {
      if (!studioTwoViewActive() || !intent || state.studioLoading || !validBase()
        || !Number.isInteger(intent.index) || intent.index < 0 || intent.index >= state.base.pickCount
        || intent.base !== state.base || intent.revision !== state.flowRevision || state.studioSlots[intent.index] !== intent.oldId || !product) return null;
      const next = new Map(Array.from(state.ingredients, ([id, entry]) => [id, { ...entry }]));
      if (intent.oldId) {
        const old = next.get(intent.oldId);
        if (!old?.qty) return null;
        if (old.qty === 1) next.delete(intent.oldId); else old.qty -= 1;
      }
      const key = String(product.id), entry = next.get(key) || { product, qty: 0 };
      entry.qty += 1; next.set(key, entry);
      if (selectedTotal(next) > state.base.pickCount || !hasPortions(product, entry.qty) || !validEntries(next, 'candy')) return null;
      return next;
    };
    const studioSlotCandidate = product => state.studioView === 'picker' ? studioSelectionForSlot(product, studioSlotIntent) : null;
    const commitStudioSlot = (product, index, next) => {
      const wasReplacement = !!studioReplacementIntent;
      studioReplacementIntent = null;
      state.studioSlots[index] = String(product.id); state.ingredients = next;
      if (state.lastAdvisorQuery) recordAffinity(state.lastAdvisorQuery, product.id);
      returnStudioCreation(false); updateMeter('ingredients'); updateFooter(); focusStudioSlot(index);
      if (wasReplacement) announce('');
    };
    const chooseStudioSlotProduct = product => {
      const next = studioSlotCandidate(product);
      if (!next) { announce(text('selection_changed', 'Die Verfügbarkeit oder Mix-Regeln haben sich geändert. Bitte prüfe deine Auswahl erneut.')); return false; }
      const index = studioSlotIntent.index;
      // Show the existing renderer before its one-portion diff starts animating.
      commitStudioSlot(product, index, next);
      return true;
    };
    const cancelStudioReplacement = (restoreFocus = true) => {
      const wasReplacement = !!studioReplacementIntent;
      const index = studioReplacementIntent?.index ?? 0;
      studioReplacementIntent = null; syncStudioMobileLayout(); renderStudioSlots();
      if (restoreFocus && studioTwoViewActive()) focusStudioSlot(index);
      if (wasReplacement) announce('');
    };
    const duplicateStudioSlot = (index, trigger) => {
      if (!studioTwoViewActive() || state.studioView !== 'creation' || studioReplacementIntent || !validBase() || state.studioLoading) return;
      reconcileStudioSlots();
      const id = state.studioSlots[index], product = state.ingredients.get(id)?.product;
      if (!Number.isInteger(index) || !product) return;
      const free = state.studioSlots.indexOf(null);
      if (free < 0) { openStudioUpgrade(trigger, { productId: id, index, base: state.base, revision: state.flowRevision }); return; }
      const next = studioSelectionForSlot(product, { index: free, oldId: null, base: state.base, revision: state.flowRevision });
      if (next) commitStudioSlot(product, free, next);
      else announce(text('selection_changed', 'Die Verfügbarkeit oder Mix-Regeln haben sich geändert. Bitte prüfe deine Auswahl erneut.'));
    };
    const chooseStudioReplacementTarget = index => {
      const intent = studioReplacementIntent;
      if (!intent || !studioTwoViewActive() || state.studioView !== 'creation' || intent.base !== state.base || intent.revision !== state.flowRevision) return;
      const product = state.products.find(item => String(item.id) === intent.productId);
      const next = studioSelectionForSlot(product, { ...intent, index, oldId: state.studioSlots[index] });
      if (next) commitStudioSlot(product, index, next);
      else announce(text('selection_changed', 'Die Verfügbarkeit oder Mix-Regeln haben sich geändert. Bitte prüfe deine Auswahl erneut.'));
    };
    const validMix = () => validBase()
      && selectedTotal(state.ingredients) === state.base.pickCount
      && validEntries(state.ingredients, mode === 'muesli' ? 'cereal' : 'candy')
      && Number.isInteger(state.base.toppingCount) && state.base.toppingCount >= 0
      && selectedTotal(state.toppings) <= state.base.toppingCount
      && validEntries(state.toppings, 'topping');
    const studioPackageDescriptor = sku => {
      const matches = (Array.isArray(previewConfig.packages) ? previewConfig.packages : []).filter(item => item?.sku === sku);
      return matches.length === 1 ? matches[0] : null;
    };
    const sameStudioPackageFamily = card => {
      const current = studioPackageDescriptor(state.base?.sku), candidate = studioPackageDescriptor(card.dataset.erpSku);
      if (current && candidate) {
        if (!['bottle', 'bag', 'cup', 'heart', 'number', 'letter'].includes(current.shape) || candidate.shape !== current.shape) return false;
        return !['number', 'letter'].includes(current.shape) || (!!current.glyph && current.glyph === candidate.glyph);
      }
      // Without an exact shape mapping only variants of the same actual
      // Shopify product are known to belong together. Never guess by title.
      return !!state.base?.productId && String(card.dataset.productId) === String(state.base.productId);
    };
    const studioUpgrades = () => {
      if (!studio || !studioCheckoutEnabled || state.demoMode || state.studioLoading || !validBase() || state.catalogError) return [];
      if (studioDuplicateIntent && (studioDuplicateIntent.base !== state.base || studioDuplicateIntent.revision !== state.flowRevision || !state.ingredients.has(studioDuplicateIntent.productId))) return [];
      const catalog = state.catalog, profile = catalog?.profiles?.candy;
      if (Number(profile?.active) !== 1) return [];
      const buffer = Math.max(0, Number(profile.stock_buffer) || 0), seen = new Set();
      return qa('[data-base-product]').filter(card => {
        const rows = (catalog.groups.packaging || []).filter(item => item.sku === card.dataset.erpSku && item.mix_type === 'candy');
        if (rows.length !== 1) return false;
        const pack = rows[0], portion = Number(card.dataset.portionGrams), limit = Number(pack.max_units), price = Number(card.dataset.price);
        if (card.disabled || card.getAttribute('aria-disabled') === 'true' || card.dataset.shopifyAvailable !== 'true' || Number(pack.active) !== 1 || Number(pack.available_quantity) < 1
          || !Number.isSafeInteger(portion) || portion <= 0 || !Number.isSafeInteger(limit) || limit <= state.base.pickCount || limit < selectedTotal(state.ingredients) + (studioDuplicateIntent ? 1 : 0)
          || Number(card.dataset.pickCount) !== limit || Number(card.dataset.portionGrams) !== portion || !Number.isSafeInteger(price) || price < 0
          || !/^[1-9]\d*$/.test(String(card.dataset.variantId)) || !sameStudioPackageFamily(card) || seen.has(card.dataset.variantId)) return false;
        const preserves = selectedList(state.ingredients).every(entry => {
          const candies = (catalog.groups.candy || []).filter(item => item.sku === entry.product._erp?.sku);
          return candies.length === 1 && Number(candies[0].active) === 1 && Number.isSafeInteger(entry.qty) && entry.qty > 0
            && Number(candies[0].available_quantity) >= (entry.qty + (studioDuplicateIntent?.productId === String(entry.product.id) ? 1 : 0)) * portion + buffer;
        });
        if (preserves) seen.add(card.dataset.variantId);
        return preserves;
      }).sort((a, b) => Number(a.dataset.pickCount) - Number(b.dataset.pickCount) || Number(a.dataset.price) - Number(b.dataset.price));
    };
    const renderStudioUpgrade = () => {
      const description = q('[data-studio-upgrade-description]'), choices = q('[data-studio-upgrade-choices]'), note = q('[data-studio-upgrade-note]');
      const upgrades = studioUpgrades();
      if (description) description.textContent = format('studio_upgrade_description', 'Deine Verpackung „{title}“ ist mit {limit} Portionen voll. Für weitere Sorten brauchst du mehr Platz.', { title: state.base?.title || '', limit: state.base?.pickCount || 0 });
      const duplicate = studioDuplicateIntent && state.products.find(product => String(product.id) === studioDuplicateIntent.productId);
      if (description && duplicate && upgrades.length) description.textContent += ' ' + format('studio_upgrade_add_one', 'Eine größere Verpackung übernimmt deinen Mix und fügt 1 Portion {title} hinzu.', { title: duplicate.title });
      const replace = q('[data-studio-upgrade-replace]');
      if (replace) { replace.hidden = !duplicate || !studioTwoViewActive() || !q('[data-studio-replace-bar]'); replace.disabled = false; }
      if (!choices) return;
      choices.replaceChildren();
      upgrades.forEach(card => {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'button button--primary'; button.dataset.studioUpgradeVariant = card.dataset.variantId;
        button.textContent = format('studio_upgrade_option', '{title} · {limit} Portionen · {price}', { title: card.dataset.title, limit: card.dataset.pickCount, price: money(card.dataset.price) });
        choices.appendChild(button);
      });
      if (note) note.textContent = upgrades.length ? text('studio_upgrade_preserve', 'Deine bisherige Auswahl bleibt erhalten. Der angezeigte Preis gilt für die größere Verpackung.') : text('studio_upgrade_empty', 'Aktuell ist keine passende größere Verpackung für deinen Mix verfügbar. Du kannst eine Portion entfernen oder zur Verpackungsauswahl wechseln.');
    };
    const openStudioUpgrade = (trigger, duplicate = null) => {
      if (!studio || state.studioLoading || !state.base) return;
      if (!openStudioDialog(q('[data-studio-upgrade]'), trigger)) return;
      studioDuplicateIntent = duplicate;
      renderStudioUpgrade();
    };
    const chooseStudioUpgrade = async variantId => {
      const dialog = q('[data-studio-upgrade]');
      const offered = studioUpgrades().find(card => card.dataset.variantId === variantId);
      if (!dialog?.open || dialog.getAttribute('aria-busy') === 'true' || !offered) return;
      const offeredRule = [offered.dataset.price, offered.dataset.pickCount, offered.dataset.portionGrams].join('|');
      const duplicate = studioDuplicateIntent;
      const revision = ++studioUpgradeRevision, flowRevision = state.flowRevision, previousBase = state.base;
      const obsolete = () => disposed || revision !== studioUpgradeRevision || flowRevision !== state.flowRevision || state.base !== previousBase || !dialog.open;
      dialog.setAttribute('aria-busy', 'true');
      q('[data-studio-upgrade-choices]')?.querySelectorAll('button').forEach(button => { button.disabled = true; });
      try {
        state.catalog = null;
        const catalog = await loadCatalog();
        if (obsolete()) return;
        await hydrateStudioBaseVariants(catalog, obsolete);
        if (obsolete()) return;
        applyCatalogToBaseProducts(catalog);
        const card = studioUpgrades().find(candidate => candidate.dataset.variantId === variantId);
        if (!card || [card.dataset.price, card.dataset.pickCount, card.dataset.portionGrams].join('|') !== offeredRule) throw new Error('unavailable');
        const details = studioBaseItems.get(card), sku = card.dataset.erpSku;
        const nextBase = { productId: card.dataset.productId, variantId: card.dataset.variantId, title: card.dataset.title, price: Number(card.dataset.price),
          pickCount: Number(card.dataset.pickCount), toppingCount: Number(card.dataset.toppingCount), packaging: card.dataset.packaging,
          packagingLabel: previousBase.packagingLabel, sku, portionGrams: Number(card.dataset.portionGrams), _erp: details?.item, _profile: details?.profile,
          _preview: studioPackageDescriptor(sku) || { ...previewDescriptor(), id: sku } };
        // Refresh stock metadata for the new portion size without changing any
        // selected SKU or quantity, and without a second ingredient-load race.
        const profile = catalog.profiles.candy;
        const products = state.products.map(product => {
          const item = catalog.groups.candy.find(row => row.sku === product._erp?.sku);
          return item && Number(item.active) === 1 ? mergeCatalogProduct({ ...item, role: 'candy', ...(candyImageOverrides.get(item.sku) ? { preview_image_url: candyImageOverrides.get(item.sku) } : {}) }, product, { portion: nextBase.portionGrams, buffer: profile.stock_buffer }) : null;
        }).filter(Boolean);
        const nextIngredients = new Map(selectedList(state.ingredients).map(entry => {
          const product = products.find(item => item._erp?.sku === entry.product._erp?.sku);
          if (!product) throw new Error('missing selected SKU');
          return [String(product.id), { product, qty: entry.qty }];
        }));
        if (duplicate) {
          if (duplicate.base !== previousBase || duplicate.revision !== flowRevision) throw new Error('obsolete portion request');
          const extra = nextIngredients.get(duplicate.productId);
          const candy = catalog.groups.candy.find(item => item.sku === extra?.product._erp?.sku);
          if (!extra || !candy || Number(candy.active) !== 1 || selectedTotal(nextIngredients) + 1 > nextBase.pickCount
            || Number(candy.available_quantity) < (extra.qty + 1) * nextBase.portionGrams + Math.max(0, Number(profile.stock_buffer) || 0)) throw new Error('extra portion unavailable');
          nextIngredients.set(duplicate.productId, { ...extra, qty: extra.qty + 1 });
        }
        state.flowRevision += 1; state.base = nextBase; state.packaging = nextBase.packaging;
        state.products = products; state.ingredients = nextIngredients; state.upsellsLoaded = false;
        qa('[data-base-product]').forEach(candidate => candidate.classList.toggle('is-selected', candidate === card));
        const priorTrigger = studioDialogTrigger;
        const triggerProductId = priorTrigger?.closest('[data-ingredient-card]')?.dataset.productId || priorTrigger?.dataset.productId;
        closeStudioDialog(false);
        renderProducts(state.products, q('[data-ingredient-grid]'), 'ingredients');
        setStep('ingredients'); applySearch(q('[data-ingredient-search]'), 'ingredients');
        const nextTrigger = priorTrigger?.isConnected ? priorTrigger : qa('[data-ingredient-card][data-kind="ingredients"]').find(candidate => candidate.dataset.productId === triggerProductId)?.querySelector('[data-card-plus]');
        if (studioTwoViewActive()) focusStudioSlot(duplicate?.index ?? 0);
        else if (nextTrigger && !nextTrigger.disabled) nextTrigger.focus({ preventScroll: true }); else q('[data-ingredient-search]')?.focus({ preventScroll: true });
        announce(format('studio_upgrade_confirmed', 'Zu „{title}“ gewechselt. Deine {total} gewählten Portionen bleiben erhalten.', { title: nextBase.title, total: selectedTotal(state.ingredients) }));
      } catch (error) {
        if (!obsolete()) {
          renderStudioUpgrade();
          const note = q('[data-studio-upgrade-note]');
          if (note) note.textContent = text('studio_upgrade_failed', 'Diese Verpackung konnte nicht bestätigt werden. Dein bisheriger Mix wurde nicht verändert. Bitte prüfe die Verpackungsauswahl.');
        }
      } finally { if (!disposed && revision === studioUpgradeRevision) dialog.setAttribute('aria-busy', 'false'); }
    };
    const hasProgress = () => Boolean(state.packaging || state.base || selectedTotal(state.ingredients) || selectedTotal(state.toppings));
    const setNext = (label, disabled) => nextButtons.forEach(button => {
      button.textContent = button === mobileNext ? text('studio_continue_label', 'Weiter') : label;
      button.disabled = disabled || state.submitting || (studio && state.studioLoading && ['size', 'ingredients'].includes(state.step)) || (studio && state.submitLocked && state.step === 'review');
    });

    const setStudioIngredientLoading = loading => {
      if (!studio || disposed) return;
      state.studioLoading = loading;
      root.dataset.studioLoading = String(loading);
      const grid = q('[data-ingredient-grid]');
      if (grid) grid.setAttribute('aria-busy', String(loading));
      // Old cards are not a usable placeholder for a different package. Remove
      // them synchronously, before any asynchronous catalog/collection read.
      if (loading) {
        qa('[data-ingredient-card][data-kind="ingredients"]').forEach(card => {
          ['[data-card-plus]', '[data-card-minus]', '[data-card-info]'].forEach(selector => { const button = card.querySelector(selector); if (button) button.disabled = true; });
        });
        if (grid) grid.innerHTML = `<p class="native-config__loading">${label('loading_label', 'Sortiment wird geladen …')}</p>`;
      }
      ['[data-ingredient-search]', '[data-native-advisor-open]', '[data-native-surprise]'].forEach(selector => { const control = q(selector); if (control) control.disabled = loading; });
      qa('[data-studio-filter]').forEach(button => { button.disabled = loading; });
      const filterSummary = q('[data-studio-filter-summary]');
      if (filterSummary) filterSummary.setAttribute('aria-disabled', String(loading));
      if (loading) closeStudioFilterMenu();
      if (loading) {
        const counter = q('[data-studio-result-count]');
        if (counter) counter.textContent = text('loading_label', 'Sortiment wird geladen …');
        const empty = q('[data-filter-empty]');
        if (empty) empty.hidden = true;
      }
    };

    const updateProgress = () => {
      const activeIndex = steps.indexOf(state.step);
      qa('[data-progress-step]').forEach(button => {
        const index = steps.indexOf(button.dataset.progressStep);
        button.classList.toggle('is-active', index === activeIndex);
        button.classList.toggle('is-complete', index < activeIndex);
        button.disabled = index > activeIndex || (index > 0 && !state.base);
      });
    };

    const renderSelectionDock = () => {
      const dock = q('[data-selection-dock]');
      const items = q('[data-selection-dock-items]');
      const count = q('[data-selection-dock-count]');
      if (!dock || !items) return;
      const currentKind = state.step === 'toppings' ? 'toppings' : 'ingredients';
      const list = currentKind === 'toppings' ? selectedList(state.toppings) : selectedList(state.ingredients);
      const atCapacity = studio && currentKind === 'ingredients' && !state.studioLoading && validBase() && selectedTotal(state.ingredients) >= state.base.pickCount;
      dock.hidden = studioTwoViewActive() || !['ingredients', 'toppings'].includes(state.step) || (!list.length && !studioMobileSelectionActive);
      if (studio) { const clear = q('[data-selection-clear]'); if (clear) clear.disabled = !list.length; }
      if (count) count.textContent = String(list.reduce((sum, entry) => sum + entry.qty, 0));
      items.innerHTML = list.map(entry => {
        const image = productImage(entry.product);
        return `<article class="native-selection-chip">
          ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : `<span class="native-selection-chip__fallback">${studio ? label('studio_no_image', 'Noch kein Produktfoto') : '🍬'}</span>`}
          <span><strong>${escapeHtml(entry.product.title)}</strong><span class="native-selection-chip__qty"><button type="button" data-selection-adjust data-delta="-1" data-kind="${currentKind}" data-product-id="${escapeHtml(entry.product.id)}" aria-label="${escapeHtml(format('less_product', 'Weniger {title}', { title: entry.product.title }))}">−</button><b>${entry.qty}</b><button type="button" data-selection-adjust data-delta="1" data-kind="${currentKind}" data-product-id="${escapeHtml(entry.product.id)}"${atCapacity ? ' class="is-at-capacity" aria-haspopup="dialog"' : ''} aria-label="${escapeHtml(atCapacity ? format('studio_capacity_action', 'Mehr {title}: Portionsgrenze erreicht, Verpackung prüfen', { title: entry.product.title }) : format('more_product', 'Mehr {title}', { title: entry.product.title }))}">+</button></span></span>
          ${studio ? '' : `<button type="button" data-selection-remove data-kind="${currentKind}" data-product-id="${escapeHtml(entry.product.id)}" aria-label="${escapeHtml(format('remove_product', '{title} entfernen', { title: entry.product.title }))}">×</button>`}
        </article>`;
      }).join('');
    };

    const updateMeter = (type = 'ingredients') => {
      const isTopping = type === 'toppings';
      const map = isTopping ? state.toppings : state.ingredients;
      const total = selectedTotal(map);
      const limit = Number(isTopping ? state.base?.toppingCount : state.base?.pickCount) || 0;
      const countEl = q(isTopping ? '[data-topping-count]' : '[data-selection-count]');
      const limitEl = q(isTopping ? '[data-topping-limit]' : '[data-selection-limit]');
      const progress = q(isTopping ? '[data-topping-progress]' : '[data-selection-progress]');
      if (countEl) countEl.textContent = total;
      if (limitEl) limitEl.textContent = limit;
      if (progress) progress.style.width = `${Math.min(100, limit ? (total / limit) * 100 : 0)}%`;
      qa(`[data-ingredient-card][data-kind="${isTopping ? 'toppings' : 'ingredients'}"]`).forEach(card => {
        const quantity = Number(map.get(card.dataset.productId)?.qty || 0);
        card.classList.toggle('is-selected', quantity > 0);
        const output = card.querySelector('[data-card-qty]');
        const plus = card.querySelector('[data-card-plus]');
        const minus = card.querySelector('[data-card-minus]');
        if (output) output.textContent = quantity;
        const product = (isTopping ? state.toppingProducts : state.products).find(item => String(item.id) === card.dataset.productId);
        const portion = Number(product?._erp?.portion_grams) > 0 ? Number(product._erp.portion_grams) : 1;
        if (plus && studio && studioTwoViewActive() && !isTopping) {
          plus.disabled = state.studioView !== 'picker' || !studioSlotCandidate(product);
          plus.classList.remove('is-at-capacity'); plus.removeAttribute('aria-haspopup');
          plus.setAttribute('aria-label', studioSlotIntent?.oldId
            ? format('studio_slot_replace', 'Candy {number}: {title} austauschen', { number: studioSlotIntent.index + 1, title: product?.title || '' })
            : format('more_product', 'Mehr {title}', { title: product?.title || '' }));
        } else if (plus && studio) {
          const atCapacity = !isTopping && limit > 0 && total >= limit && !state.studioLoading && validBase() && hasPortions(product, Math.max(1, quantity));
          plus.disabled = !atCapacity && (total >= limit || !state.base || !hasPortions(product, quantity + 1));
          plus.classList.toggle('is-at-capacity', atCapacity);
          if (atCapacity) plus.setAttribute('aria-haspopup', 'dialog'); else plus.removeAttribute('aria-haspopup');
          plus.setAttribute('aria-label', atCapacity ? format('studio_capacity_action', 'Mehr {title}: Portionsgrenze erreicht, Verpackung prüfen', { title: product.title }) : format('more_product', 'Mehr {title}', { title: product?.title || '' }));
        } else if (plus) plus.disabled = total >= limit || !state.base || !product?.variants?.some(variant => variant.available)
          || Number(product?._erp?.available_quantity || 0) < (quantity + 1) * portion;
        if (studio && minus) minus.disabled = studioTwoViewActive() || state.studioLoading || quantity <= 0;
      });
      renderStudioSlots();
      renderSelectionDock();
      updateCandyPreview();
      return { total, limit };
    };

    const updateFooter = () => {
      const index = steps.indexOf(state.step);
      backButtons.forEach(button => { button.disabled = index === 0 || state.submitting; });
      if (state.submitting) { setNext(text('adding_label', 'Mix wird vorbereitet …'), true); return; }
      footerTotal.textContent = state.base && !state.demoMode ? money(state.base.price) : '';
      if (studio && state.studioLoading && ['size', 'ingredients'].includes(state.step)) {
        footerLabel.textContent = text('loading_label', 'Sortiment wird geladen …');
        setNext(text('loading_label', 'Sortiment wird geladen …'), true); return;
      }
      if (studio && state.submitLocked && state.step === 'review') {
        footerLabel.textContent = !normalShopifyCart && state.catalog?.checkout?.mode === 'erp-reserved' ? 'Dein Checkout wurde bereits begonnen. Nutze „Begonnene Bestellung fortsetzen“.' : text('studio_cart_uncertain', 'Die Rückmeldung ist unklar. Bitte prüfe deinen Warenkorb, bevor du erneut bestellst. Dieser Mix bleibt vorsichtshalber gesperrt.');
        setNext(!normalShopifyCart && state.catalog?.checkout?.mode === 'erp-reserved' ? 'Bestellung bereits begonnen' : text('studio_check_cart', 'Warenkorb zuerst prüfen'), true); return;
      }
      if (state.step === 'packaging') {
        footerLabel.textContent = state.packaging ? text('packaging_selected', 'Verpackung gewählt') : text('packaging_first', 'Wähle zuerst eine Verpackung');
        setNext(text('next_size_label', 'Größe wählen →'), !state.packaging || (!state.inventoryReady && !demoReady()) || !state.catalog || !!state.catalogError);
      } else if (state.step === 'size') {
        footerLabel.textContent = state.base ? state.base.title : text('choose_size', 'Wähle deine Größe');
        setNext(mode === 'muesli' ? text('next_muesli_label', 'Zutaten wählen →') : text('next_candy_label', 'Candys wählen →'), !validBase());
      } else if (state.step === 'ingredients') {
        const meter = updateMeter('ingredients');
        footerLabel.textContent = format('selection_count', '{total} von {limit} ausgewählt', meter);
        setNext(mode === 'muesli' ? text('next_toppings_label', 'Zu den Toppings →') : text('review_label', 'Mix prüfen →'), !validBase() || meter.limit <= 0 || meter.total !== meter.limit || !validEntries(state.ingredients, mode === 'muesli' ? 'cereal' : 'candy'));
      } else if (state.step === 'toppings') {
        const meter = updateMeter('toppings');
        footerLabel.textContent = format('topping_selection_count', '{total} von bis zu {limit} Toppings', meter);
        setNext(text('review_label', 'Müsli prüfen →'), !validMix());
      } else {
        footerLabel.textContent = state.base?.title || text('your_mix', 'Dein Mix');
        if (studio && state.demoMode) {
          footerLabel.textContent = text('studio_preview_only', 'Vorschau – noch nicht bestellbar');
          setNext(text('studio_preview_only', 'Vorschau – noch nicht bestellbar'), true);
        } else setNext(studio && !normalShopifyCart && state.catalog?.checkout?.mode === 'erp-reserved' ? 'In den Mix-Warenkorb' : text('cart_label', 'Mix in den Warenkorb'), !validMix() || (!studio && !q('[data-native-terms]')?.checked));
      }
    };

    const setStep = step => {
      if (disposed || state.submitting || !steps.includes(step)) return;
      if (studio && step !== state.step) {
        studioViewRevision += 1; state.studioView = 'creation'; state.studioTargetSlot = null; studioSlotIntent = null;
      }
      if (studio && step === 'review' && state.step !== 'review') announce('');
      state.step = step;
      if (studio) root.dataset.studioStep = step;
      if (studio) {
        // Reuse the exact filled scene, including its current particle positions.
        const preview = q('[data-candy-preview]');
        const destination = q(step === 'review' ? '[data-review-preview]' : '.candy-studio__stage');
        if (preview && destination && preview.parentNode !== destination) destination.appendChild(preview);
      }
      qa('[data-screen]').forEach(screen => {
        const active = screen.dataset.screen === step;
        screen.hidden = !active;
        screen.classList.toggle('is-active', active);
      });
      updateProgress();
      updateFooter();
      syncStudioMobileLayout();
      renderSelectionDock();
      updateCandyPreview();
      root.querySelector('.native-config__workspace')?.scrollTo?.({ top: 0, behavior: 'instant' });
    };

    const filterBaseProducts = () => {
      let visible = 0;
      const cards = qa('[data-base-wrap]');
      cards.forEach(wrapper => {
        const card = wrapper.querySelector('[data-base-product]');
        const show = !state.demoMode && wrapper.dataset.packaging === state.packaging && !card?.classList.contains('is-soldout');
        wrapper.hidden = !show;
        if (show) visible += 1;
      });
      qa('[data-demo-wrap]').forEach(wrapper => {
        const show = state.demoMode && wrapper.dataset.packaging === state.packaging;
        wrapper.hidden = !show;
        if (show) visible += 1;
      });
      const empty = q('[data-base-empty]');
      if (empty) empty.hidden = visible > 0;
    };

    const renderProducts = (products, grid, kind) => {
      if (!grid) return;
      if (!products.length) {
        grid.innerHTML = `<p class="native-config__empty">${label('products_empty', 'Hier sind gerade keine verfügbaren Produkte hinterlegt.')}</p>`;
        return;
      }
      grid.innerHTML = products.map(product => {
        const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
        if (!variant) return '';
        const tags = productTags(product);
        const badges = tags.filter(tag => /vegan|halal|sauer|zuckerfrei|frucht|schoko|crunch/i.test(tag)).slice(0, 2).map(tag => tag.replace(/^ck:/i, ''));
        const image = productImage(product);
        const demoNote = studio && state.demoMode && !variant.available;
        const availabilityId = demoNote ? `NativeCandyAvailability-${encodeURIComponent(root.dataset.sectionId || 'configurator')}-${encodeURIComponent(kind)}-${encodeURIComponent(product.id)}` : '';
        const availabilityDescription = demoNote ? ` aria-describedby="${escapeHtml(availabilityId)}"` : '';
        return `<article class="native-ingredient" data-ingredient-card data-kind="${kind}" data-product-id="${escapeHtml(product.id)}" data-search="${escapeHtml(searchableProduct(product))}"${availabilityDescription}>
          <button class="native-ingredient__info" type="button" data-card-info aria-label="${escapeHtml(format('product_info_label', 'Informationen zu {title}', { title: product.title }))}">i</button>
          <div class="native-ingredient__media">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : `<span>${studio ? label('studio_no_image', 'Noch kein Produktfoto') : '🍬'}</span>`}</div>
          <div class="native-ingredient__copy"><div class="native-ingredient__badges">${badges.map(tag => `<span>${escapeHtml(tag)}</span>`).join('')}</div><h3>${escapeHtml(product.title)}</h3>${!variant.available ? `<p class="native-ingredient__unavailable"${demoNote ? ` data-studio-demo-note id="${escapeHtml(availabilityId)}"` : ''}>${demoNote ? Number(product._erp?.available_quantity || 0) <= 0 || product._erp?.is_available === false ? label('studio_unavailable_preview', 'Nur Vorschau · aktuell nicht lieferbar') : label('studio_preview_only', 'Vorschau – noch nicht bestellbar') : label('sold_out', 'Aktuell nicht verfügbar')}</p>` : ''}</div>
          <div class="native-ingredient__quantity"><button type="button" data-card-minus aria-label="${escapeHtml(format('less_product', 'Weniger {title}', { title: product.title }))}">−</button><strong data-card-qty>0</strong><button type="button" data-card-plus${availabilityDescription} aria-label="${escapeHtml(format('more_product', 'Mehr {title}', { title: product.title }))}">+</button></div>
        </article>`;
      }).join('');
    };

    const ensureProducts = async kind => {
      const isTopping = kind === 'toppings';
      const productsKey = isTopping ? 'toppingProducts' : 'products';
      if (state[productsKey].length) return state[productsKey];
      const requestedRevision = state.flowRevision;
      const requestedBase = state.base;
      const obsolete = () => disposed || (studio && (requestedRevision !== state.flowRevision || requestedBase !== state.base));
      if (studio && !isTopping) {
        setStudioIngredientLoading(true);
        updateMeter('ingredients');
        updateFooter();
      }
      try {
        const [catalog, shopifyProducts] = await Promise.all([
          studio && state.demoMode ? loadBrowseCatalog() : loadCatalog(),
          loadCollection(isTopping ? root.dataset.toppingCollection : root.dataset.ingredientCollection).catch(() => [])
        ]);
        const group = isTopping ? 'topping' : (mode === 'muesli' ? 'cereal' : 'candy');
        const profile = catalog?.profiles?.[mode === 'muesli' ? 'cereal' : 'candy'] || {};
        const shopifyBySku = new Map();
        const shopifyByTitle = new Map();
        shopifyProducts.forEach(product => {
          (product.variants || []).forEach(variant => {
            if (variant.sku) {
              const key = studio ? String(variant.sku) : String(variant.sku).trim().toLowerCase();
              shopifyBySku.set(key, studio && shopifyBySku.has(key) && shopifyBySku.get(key) !== product ? null : product);
            }
          });
          shopifyByTitle.set(normalizeKey(product.title), product);
        });
        if (obsolete()) return state[productsKey];
        state[productsKey] = (catalog?.groups?.[group] || [])
          .filter(item => Number(item.active) === 1 && (!studio || !!item.sku))
          .filter(item => studio ? state.demoMode || (Number(profile.active) === 1 && (Number(profile.hide_sold_out ?? 1) !== 1 || Number(item.available_quantity || 0) >= Number(state.base?.portionGrams) + Math.max(0, Number(profile.stock_buffer) || 0))) : Number(profile.hide_sold_out ?? 1) !== 1 || Number(item.available_quantity || 0) >= (Number(item.portion_grams) > 0 ? Number(item.portion_grams) : 1))
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0) || String(a.title || '').localeCompare(String(b.title || ''), 'de'))
          .map(item => mergeCatalogProduct(
            { ...item, role: group, ...(studio && candyImageOverrides.get(String(item.sku)) ? { preview_image_url: candyImageOverrides.get(String(item.sku)) } : {}) },
            shopifyBySku.get(studio ? String(item.sku || '') : String(item.sku || '').trim().toLowerCase()) || (!studio ? shopifyByTitle.get(normalizeKey(item.title)) : null),
            studio ? { portion: state.demoMode ? 0 : state.base?.portionGrams, buffer: profile.stock_buffer } : null
          ));
        if (studio && !isTopping) setStudioIngredientLoading(false);
        renderProducts(state[productsKey], q(isTopping ? '[data-topping-grid]' : '[data-ingredient-grid]'), kind);
        updateMeter(kind);
        if (studio) applySearch(q('[data-ingredient-search]'), 'ingredients');
      } catch (error) {
        const grid = q(isTopping ? '[data-topping-grid]' : '[data-ingredient-grid]');
        if (!obsolete() && grid) grid.innerHTML = `<p class="native-config__empty">${label('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.')}</p>`;
      } finally {
        if (studio && !isTopping && !obsolete()) { setStudioIngredientLoading(false); updateFooter(); }
      }
      return state[productsKey];
    };

    const studioFilterValues = ['', 'fruchtig', 'sauer', 'schoko', 'vegan', 'halal'];
    const matchesStudioFilter = (product, filter) => {
      if (!studioFilterValues.includes(filter)) return false;
      if (!filter) return true;
      if (['vegan', 'halal'].includes(filter)) return !!product && matchesTerm(product, filter);
      const flavorTests = { fruchtig: /frucht|fruit|erdbeer|strawberry|beere|berry|apfel|apple|kirsche|cherry|mango|ananas|pineapple|pfirsich|peach|orange|zitrone|lemon|banan/i, sauer: /sauer|sour|acid/i, schoko: /schoko|choco|kakao|cocoa/i };
      return !!product && !!flavorTests[filter]?.test(searchableProduct(product));
    };
    const eligibleStudioRandomProducts = () => state.products.filter(product => matchesStudioFilter(product, studioRandomPreference) && hasPortions(product, 1)
      && validEntries(new Map([[String(product.id), { product, qty: 1 }]]), 'candy'));
    const updateStudioRandom = () => {
      const dialog = q('[data-studio-random-dialog]');
      if (!dialog?.open) return;
      const available = eligibleStudioRandomProducts();
      const note = q('[data-studio-random-note]'), create = q('[data-studio-random-create]');
      if (note) note.textContent = !studioSlotCapacitySupported()
        ? text('studio_random_capacity', 'Diese Verpackungsgröße lässt sich hier nicht automatisch füllen. Bitte wähle die Portionen einzeln.')
        : available.length ? '' : text('studio_random_empty', 'Für diese Auswahl sind aktuell keine passenden Sorten verfügbar.');
      if (create) create.disabled = state.studioLoading || !validBase() || !studioSlotCapacitySupported() || !available.length;
      qa('[data-studio-random-preference]').forEach(input => { input.checked = input.value === studioRandomPreference; });
    };
    const openStudioRandom = trigger => {
      if (!studio || state.step !== 'ingredients' || state.studioLoading || !validBase() || studioReplacementIntent) return;
      if (!openStudioDialog(q('[data-studio-random-dialog]'), trigger)) return;
      studioRandomIntent = { base: state.base, revision: state.flowRevision };
      const description = q('[data-studio-random-description]');
      if (description) description.textContent = text('studio_random_description', 'Wähle eine Richtung für deinen Mix. Erst mit „Mix erstellen“ wird deine bisherige Auswahl ersetzt.');
      updateStudioRandom();
    };
    const createStudioRandom = () => {
      const intent = studioRandomIntent, dialog = q('[data-studio-random-dialog]'), note = q('[data-studio-random-note]');
      if (!dialog?.open || !intent) return;
      if (intent.base !== state.base || intent.revision !== state.flowRevision || state.step !== 'ingredients' || state.studioLoading || !validBase()) {
        if (note) note.textContent = text('selection_changed', 'Die Verfügbarkeit oder Mix-Regeln haben sich geändert. Bitte prüfe deine Auswahl erneut.');
        return;
      }
      if (!studioSlotCapacitySupported()) { updateStudioRandom(); return; }
      const available = eligibleStudioRandomProducts().slice().sort(() => Math.random() - .5);
      const next = new Map(), limit = state.base.pickCount;
      let total = 0, added = true;
      while (total < limit && added) {
        added = false;
        for (const product of available) {
          const key = String(product.id), qty = Number(next.get(key)?.qty || 0) + 1;
          if (total >= limit) break;
          if (hasPortions(product, qty) && validEntries(new Map([[key, { product, qty }]]), 'candy')) { next.set(key, { product, qty }); total += 1; added = true; }
        }
      }
      if (total !== limit || !validEntries(next, 'candy')) {
        if (note) note.textContent = available.length
          ? text('studio_random_insufficient', 'Für eine vollständige Füllung reicht die verfügbare Menge nicht. Deine Auswahl bleibt erhalten.')
          : text('studio_random_empty', 'Für diese Auswahl sind aktuell keine passenden Sorten verfügbar.');
        return;
      }
      const trigger = studioDialogTrigger;
      closeStudioDialog(false);
      state.ingredients = next; state.studioSlots = [];
      if (studioTwoViewActive()) returnStudioCreation(false);
      updateMeter('ingredients'); updateFooter();
      if (studioTwoViewActive()) focusStudioSlot(0); else trigger?.focus?.({ preventScroll: true });
      announce(format('studio_surprise_selected', 'Überraschungsmix bereit. In „{selection}“ kannst du ihn ändern.', { selection: text('selection_label', 'Deine Auswahl') }));
    };

    const applySearch = (input, kind, terms = []) => {
      if (studio && state.studioLoading && kind === 'ingredients') return;
      const value = String(input?.value || '').trim().toLowerCase();
      const groups = terms.length ? terms.map(term => term.toLowerCase().split(/\s+/).filter(Boolean)) : [];
      const words = value.split(/\s+/).filter(Boolean);
      let visible = 0;
      const cards = qa(`[data-ingredient-card][data-kind="${kind}"]`);
      cards.forEach(card => {
        const search = card.dataset.search || '';
        const product = (kind === 'toppings' ? state.toppingProducts : state.products).find(item => String(item.id) === card.dataset.productId);
        const matchesSearch = groups.length
          ? terms.every((term, index) => dietaryFieldFor(term) && product?._erp?.role === 'candy'
            ? matchesTerm(product, term) : groups[index].some(word => product ? matchesTerm(product, word) : search.includes(word)))
          : words.every(word => product ? matchesTerm(product, word) : search.includes(word));
        const filter = studio && kind === 'ingredients' ? state.studioFilter : '';
        const matchesFilter = !filter || matchesStudioFilter(product, filter);
        const show = matchesSearch && matchesFilter;
        card.hidden = !show;
        if (show) visible += 1;
      });
      const empty = kind === 'ingredients' ? q('[data-filter-empty]') : null;
      if (empty) empty.hidden = visible > 0;
      if (studio && kind === 'ingredients') {
        const counter = q('[data-studio-result-count]');
        if (counter) counter.textContent = format('studio_result_count', '{visible} von {total} Sorten', { visible, total: cards.length });
      }
    };

    const closeStudioFilterMenu = (restoreFocus = false) => {
      if (!studio) return;
      const menu = q('[data-studio-filter-menu]');
      if (studioTwoViewActive() && state.studioView === 'picker' && studioPickerFilterWasOpen !== null) { if (menu) menu.open = true; return; }
      if (menu) menu.open = false;
      if (restoreFocus) q('[data-studio-filter-summary]')?.focus();
    };
    const updateStudioFilterMenu = () => {
      if (!studio) return;
      const selectedLabel = q('[data-studio-filter-label]');
      qa('[data-studio-filter]').forEach(button => {
        const active = button.dataset.studioFilter === state.studioFilter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        // Labels are already translated by Liquid. Never parse merchant text as HTML.
        if (active && selectedLabel) selectedLabel.textContent = button.textContent.trim();
      });
    };

    const resetDiscoveryFilters = () => {
      state.advisorTerms = [];
      state.lastAdvisorQuery = '';
      if (studio) {
        state.studioFilter = '';
        updateStudioFilterMenu();
        closeStudioFilterMenu();
      }
      qa('[data-advisor-term]').forEach(node => node.classList.remove('is-selected'));
      const ingredientSearch = q('[data-ingredient-search]');
      const toppingSearch = q('[data-topping-search]');
      const favoriteSearch = q('[data-advisor-favorite]');
      if (ingredientSearch) ingredientSearch.value = '';
      if (toppingSearch) toppingSearch.value = '';
      if (favoriteSearch) favoriteSearch.value = '';
      const advisorResults = q('[data-advisor-results]');
      if (advisorResults) advisorResults.innerHTML = '';
      applySearch(ingredientSearch, 'ingredients');
      applySearch(toppingSearch, 'toppings');
    };

    const tasteProfile = () => {
      const profiles = [
        { key: 'sauer', test: /sauer|sour|acid/i, search: 'sauer', heading: 'Noch mehr Sauer-Power für später?' },
        { key: 'cola', test: /cola|kola/i, search: 'Cola', heading: 'Noch mehr Cola-Vibes für später?' },
        { key: 'tropisch', test: /trop|ananas|pineapple|mango|passion/i, search: 'tropisch', heading: 'Noch mehr Tropen-Vibes für später?' },
        { key: 'erdbeer', test: /erdbeer|strawberry/i, search: 'Erdbeer', heading: 'Noch mehr Erdbeer-Liebe für später?' },
        { key: 'blau', test: /blau|blue|schlumpf/i, search: 'blau', heading: 'Noch mehr blaue Lieblingssnacks?' },
        { key: 'schoko', test: /schoko|choco|kakao/i, search: 'Schokolade', heading: 'Noch mehr Schoko-Glück für später?' },
        { key: 'fruchtig', test: /frucht|fruit|gummi|bär|bear/i, search: 'Fruchtgummi', heading: 'Noch mehr fruchtige Entdeckungen?' }
      ];
      const scores = profiles.map(profile => ({ ...profile, score: selectedList(state.ingredients).reduce((sum, entry) => sum + (profile.test.test(searchableProduct(entry.product)) ? entry.qty : 0), 0) }));
      return scores.sort((a, b) => b.score - a.score)[0] || null;
    };

    const fetchSuggestedProducts = async profile => {
      if (!profile?.score) return [];
      const queries = [profile.search, `${profile.search} Snack`, `${profile.search} Süßigkeit`];
      const suggestionPages = await Promise.all(queries.map(async query => {
        try {
          const response = await request(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`, { headers: { Accept: 'application/json' } });
          return response.ok ? (await response.json())?.resources?.results?.products || [] : [];
        } catch (error) { return []; }
      }));
      const products = Array.from(new Map(suggestionPages.flat().map(product => [String(product.id || product.url), product])).values());
      const hydrated = await Promise.all(products.slice(0, 24).map(async product => {
        const handle = String(product.url || '').match(/\/products\/([^?]+)/)?.[1];
        if (!handle) return product;
        try {
          const detail = await request(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          return detail.ok ? await detail.json() : product;
        } catch (error) { return product; }
      }));
      const paid = hydrated.filter(product => {
        const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
        return String(product.id) !== String(state.base?.productId) && Number(variant?.price || product.price || 0) > 0;
      });
      if (paid.length >= 4) return paid;
      const collectionMatches = [];
      try {
        const collectionResponse = await request('/collections.json?limit=250', { headers: { Accept: 'application/json' } });
        const collections = collectionResponse.ok ? (await collectionResponse.json()).collections || [] : [];
        const relevant = collections.filter(collection => profile.test.test(`${collection.title || ''} ${collection.handle || ''}`)).slice(0, 2);
        for (const collection of relevant) collectionMatches.push(...await loadCollection(collection.handle));
      } catch (error) {}
      const catalog = [];
      for (let page = 1; page <= 2 && catalog.length < 4; page += 1) {
        try {
          const catalogResponse = await request(`/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
          if (!catalogResponse.ok) break;
          const pageProducts = (await catalogResponse.json()).products || [];
          catalog.push(...pageProducts.filter(product => searchableProduct(product).includes(profile.search.toLowerCase())));
          if (pageProducts.length < 250) break;
        } catch (error) { break; }
      }
      const unique = new Map([...paid, ...collectionMatches, ...catalog].map(product => [String(product.id), product]));
      return Array.from(unique.values()).filter(product => {
        const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
        return String(product.id) !== String(state.base?.productId) && Number(variant?.price || product.price || 0) > 0;
      });
    };

    const loadUpsells = async () => {
      const section = q('[data-native-upsells]');
      const grid = q('[data-upsell-grid]');
      if (studio) { if (section) section.hidden = true; return; }
      if (!section || !grid || !state.base?.productId || state.upsellsLoaded) return;
      state.upsellsLoaded = true;
      const profile = tasteProfile();
      const heading = q('[data-upsell-heading]');
      if (heading) heading.textContent = text('upsell_heading', 'Vielleicht schmeckt dir das auch.');
      try {
        let products = await fetchSuggestedProducts(profile);
        if (!products.length && !profile?.score) {
          const response = await request(`/recommendations/products.json?product_id=${encodeURIComponent(state.base.productId)}&limit=4&intent=related`);
          if (response.ok) products = (await response.json()).products || [];
        }
        grid.innerHTML = products.slice(0, 4).map(product => {
          const variant = (product.variants || []).find(item => item.available);
          if (!variant) return '';
          const image = productImage(product);
          return `<article class="native-upsell-card">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span>🍭</span>'}<div><strong>${escapeHtml(product.title)}</strong><small>${money(variant.price || product.price)}</small></div><button type="button" data-upsell-add data-variant-id="${escapeHtml(variant.id)}">${label('upsell_add', '+ Dazu')}</button></article>`;
        }).join('');
        section.hidden = !grid.innerHTML.trim();
      } catch (error) {
        section.hidden = true;
      }
    };

    const showReview = () => {
      const productBox = q('[data-review-product]');
      if (productBox && state.base) {
        const descriptor = studio ? previewDescriptor() : null;
        const image = safeUrl(descriptor?.image_url || descriptor?.front_image_url);
        const visual = studio ? image ? `<img class="native-review__icon" src="${escapeHtml(image)}" alt="">` : '' : `<span class="native-review__icon">${state.base.packaging === 'flasche' ? '🫙' : state.base.packaging === 'buchstaben_zahlen' ? 'A♥7' : state.base.packaging === 'sonderform' ? '💖' : '🍬'}</span>`;
        productBox.innerHTML = studio
          ? `<h3>${escapeHtml(state.base.title)}</h3>`
          : `${visual}<div><small>${escapeHtml(state.base.packagingLabel || '')}</small><h3>${escapeHtml(state.base.title)}</h3><strong>${state.demoMode ? label('studio_preview_only', 'Vorschau – noch nicht bestellbar') : money(state.base.price)}</strong></div>`;
      }
      const ingredientBox = q('[data-review-ingredients]');
      if (ingredientBox) ingredientBox.innerHTML = selectedList(state.ingredients).map(entry => {
        if (!studio) return `<span>${entry.qty}× ${escapeHtml(entry.product.title)}</span>`;
        const image = safeUrl(entry.product._erp?.preview_image_url) || productImage(entry.product);
        return `<div class="native-review__candy">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="72" height="64">` : ''}<span>${escapeHtml(entry.product.title)}</span><strong>${entry.qty}×</strong></div>`;
      }).join('') || `<span>${label('selection_empty', 'Keine Auswahl')}</span>`;
      const toppingBox = q('[data-review-toppings]');
      if (toppingBox) toppingBox.innerHTML = selectedList(state.toppings).map(entry => `<span>${entry.qty}× ${escapeHtml(entry.product.title)}</span>`).join('') || `<span>${label('toppings_empty', 'Keine Toppings')}</span>`;
      const price = q('[data-review-price]');
      if (price) { price.textContent = state.demoMode ? '' : money(state.base?.price || 0); price.hidden = state.demoMode; }
      const terms = q('[data-native-terms]');
      if (terms) { terms.checked = false; if (studio) { terms.disabled = false; const legal = terms.closest('.native-config__legal'); if (legal) legal.hidden = false; } }
      clearTermsError();
      if (studio) { const summary = q('.native-review__summary'); if (summary) summary.hidden = state.demoMode; }
      state.upsellsLoaded = false;
      loadUpsells();
    };

    const clearTermsError = () => {
      const terms = q('[data-native-terms]'), error = q('[data-native-terms-error]');
      terms?.removeAttribute('aria-invalid');
      terms?.closest('.native-config__legal')?.classList.remove('is-invalid');
      if (error) error.hidden = true;
    };
    let resumeMixBox = null;
    const clearMixCart = (message,id=readPending()?.request?.id) => {
      if(id)writeMixBasket(readMixBasket().filter(row=>row.request.id!==id));
      if(!id||readPending()?.request?.id===id)sessionStorage.removeItem(pendingKey);
      state.submitLocked=!!readPending()?.request;state.pendingNonce=readPending()?.nonce||'';
      if(mixCartTimer)clearTimeout(mixCartTimer);
      if(resumeMixBox)resumeMixBox.hidden=true;
      if(message)announce(message);
    };
    const refreshMixCartTimer = () => {
      if(mixCartTimer)clearTimeout(mixCartTimer);
      const rows=readMixBasket(),pending=readPending()||rows[0];if(disposed||!pending?.request||pending.cartState!=='reserved')return;
      const earliest=rows.length?rows.reduce((a,b)=>a.expiresAt<b.expiresAt?a:b):pending;
      const seconds=Math.max(0,Math.ceil((earliest.expiresAt-Date.now())/1000));
      const message=resumeMixBox?.querySelector('p');
      if(message)message.textContent=`Dein Mix wird 30 Minuten für dich reserviert. ${rows.length||1} Mix${rows.length===1?'':'e'} im Warenkorb · ${money(rows.length?rows.reduce((n,row)=>n+row.priceMinor,0):pending.priceMinor)}. Erste Reservierung: noch ${Math.floor(seconds/60)}:${String(seconds%60).padStart(2,'0')}. Alle Mixe werden gemeinsam bestellt; andere Shop-Artikel getrennt.`;
      if(seconds===0){
        if(!state.submitting)beginReservedCheckout(earliest.request,'status');
        else mixCartTimer=setTimeout(refreshMixCartTimer,1000);
      }else mixCartTimer=setTimeout(refreshMixCartTimer,1000);
    };
    const showMixResume = () => {
      if(normalShopifyCart)return;
      const rows=readMixBasket(),current=readPending()||rows[0];
      if (!studio || !current?.request) {if(resumeMixBox)resumeMixBox.hidden=true;return;}
      if (!resumeMixBox) {
        resumeMixBox = document.createElement('div'); resumeMixBox.className = 'native-config__empty';
        const message = document.createElement('p'); message.textContent = 'Dein Mix wird separat von anderen Shop-Artikeln bestellt. Eine begonnene Bestellung bleibt gespeichert.';
        const button = document.createElement('button'); button.type = 'button'; button.className = 'button button--primary'; button.textContent = 'Begonnene Bestellung fortsetzen';
        on(button, 'click', () => {const p=readPending()||readMixBasket()[0];beginReservedCheckout(p?.request,p?.cartState==='reserved'?'checkout':p?.action||'status');});
        const remove=document.createElement('button');remove.type='button';remove.className='button button--secondary';remove.textContent='Mix aus Warenkorb entfernen';
        on(remove,'click',()=>beginReservedCheckout((readPending()||readMixBasket()[0])?.request,'cancel'));
        const another=document.createElement('button');another.type='button';another.className='button button--secondary';another.textContent='Weiteren Mix zusammenstellen';
        on(another,'click',()=>{if(state.submitting||(readPending()&&readPending().cartState!=='reserved'))return;sessionStorage.removeItem(pendingKey);state.submitLocked=false;state.pendingNonce='';resetFlow();showMixResume();});
        const list=document.createElement('div');list.setAttribute('data-mix-basket-list','');
        resumeMixBox.appendChild(message); resumeMixBox.appendChild(button);resumeMixBox.appendChild(remove);resumeMixBox.appendChild(another);resumeMixBox.appendChild(list); root.appendChild(resumeMixBox);
      }
      resumeMixBox.hidden = false;
      const pending=current,buttons=resumeMixBox.querySelectorAll('button');
      if(buttons[0])buttons[0].textContent=pending?.cartState==='reserved'?'Jetzt zur sicheren Kasse':'Begonnene Bestellung fortsetzen';
      if(buttons[1])buttons[1].hidden=pending?.cartState!=='reserved';
      if(buttons[2]){buttons[2].hidden=pending?.cartState!=='reserved'||rows.length>=20;buttons[2].disabled=state.submitting;}
      const list=resumeMixBox.querySelector('[data-mix-basket-list]');
      if(list){list.replaceChildren();rows.forEach((row,index)=>{const line=document.createElement('p');line.textContent=`${index+1}. ${row.title||row.request.packagingSku} · ${money(row.priceMinor)}`;if(pending.cartState==='reserved'){const remove=document.createElement('button');remove.type='button';remove.className='button button--secondary';remove.textContent=`Mix ${index+1} entfernen`;remove.disabled=state.submitting;on(remove,'click',()=>beginReservedCheckout(row.request,'cancel'));line.appendChild(remove);}list.appendChild(line);});}
      refreshMixCartTimer();
    };
    const beginReservedCheckout = async (existing,action='reserve') => {
      if (disposed || state.submitting) return;
      const input = existing || {id:window.crypto.randomUUID(), packagingSku:state.base.sku,
        selections:selectedList(state.ingredients).map(entry=>({sku:entry.product._erp.sku,quantity:entry.qty}))};
      if(action==='reserve'&&!existing&&readMixBasket().length>=20){announce('Bitte zuerst die bis zu 20 Mixe im Warenkorb bestellen.');return;}
      const previous=readPending()?.request?.id===input.id?readPending():readMixBasket().find(row=>row.request.id===input.id);
      const mixIds=action==='checkout'?(previous?.mixIds||(previous?.cartState==='processing'?null:readMixBasket().map(row=>row.request.id))):null;
      // The leader and exact immutable basket order survive a lost reply/reload.
      if(mixIds?.length&&mixIds[0]!==input.id){const leader=readMixBasket().find(row=>row.request.id===mixIds[0]);if(leader)return beginReservedCheckout(leader.request,action);}
      // Never dispatch unless the identical request can survive a reload.
      try { sessionStorage.setItem(pendingKey, JSON.stringify({...previous,...(action==='checkout'?{cartState:'processing',mixIds:mixIds?.length?mixIds:undefined}:{}),nonce:input.id,request:input,action,title:previous?.title||state.base?.title||input.packagingSku,createdAt:previous?.createdAt||Date.now()})); }
      catch { announce('Bitte erlaube den Sitzungsspeicher im Browser, damit deine Bestellung nicht doppelt angelegt wird.'); return; }
      state.pendingNonce=input.id; state.submitLocked=true; state.submitting=true; root.setAttribute('aria-busy','true'); updateFooter();
      showMixResume(); const resumeButton=resumeMixBox?.querySelector('button'); if(resumeButton)resumeButton.disabled=true;
      const progress=resumeMixBox?.querySelector('p');
      const busyMessage=action==='checkout'?'Deine reservierte Auswahl wird an Shopify übergeben. Die sichere Kasse öffnet sich anschließend automatisch.':action==='reserve'?'Wir prüfen deine Auswahl und reservieren die Candys für dich.':'Dein Mix-Warenkorb wird aktualisiert.';
      if(progress)progress.textContent=busyMessage;
      if(resumeButton)resumeButton.textContent=action==='checkout'?'Kasse wird geöffnet …':action==='reserve'?'Mix wird reserviert …':'Bitte warten …';
      const slowTimer=setTimeout(()=>{if(!disposed&&state.submitting&&progress)progress.textContent=`${busyMessage} Es dauert gerade länger als üblich. Bitte nicht erneut bestellen – dein Vorgang bleibt gespeichert.`;},8000);
      try {
        const response=await request('https://erp.snackshark.de/api/public/configurator/checkout', {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({...input,action,...(mixIds?.length?{mixIds}:{})})});
        const result=await response.json();
        if(!response.ok){
          if(result.basketEditable===true){writeMixBasket(readMixBasket().filter(row=>!(result.expiredMixIds||[]).includes(row.request.id)));sessionStorage.removeItem(pendingKey);state.submitLocked=false;state.pendingNonce='';}
          if(result.notReserved===true){
            clearMixCart();
            if(Array.isArray(result.unavailable)&&result.unavailable.length){
              const missing=new Set(result.unavailable.map(item=>item.sku));
              const offer=document.createElement('button');offer.type='button';offer.className='button button--primary';offer.textContent='Nicht verfügbare Sorte ersetzen';
              on(offer,'click',()=>{
                for(const [id,entry] of state.ingredients)if(missing.has(entry.product._erp?.sku)){
                  state.ingredients.delete(id);entry.product._erp.available_quantity=0;
                }
                offer.remove();setStep('ingredients');updateMeter('ingredients');updateFooter();
                renderProducts(state.products,q('[data-ingredient-grid]'),'ingredients');
                announce('Die übrigen Sorten bleiben erhalten. Bitte fülle die freien Plätze mit einer anderen Sorte.');
              });root.appendChild(offer);offer.scrollIntoView?.({block:'center',behavior:'smooth'});
            }
          }
          throw Error(result.error || 'Die Bestellung konnte nicht bestätigt werden. Bitte den begonnenen Checkout fortsetzen.');
        }
        if(result.completed===true){const completedIds=readPending()?.mixIds||[input.id];writeMixBasket(readMixBasket().filter(row=>!completedIds.includes(row.request.id)));clearMixCart('Diese Bestellung wurde bereits übernommen. Du kannst jetzt einen neuen Mix zusammenstellen.');return;}
        if(result.state==='released'){clearMixCart('Die Reservierung wurde aufgehoben. Dein Mix ist nicht mehr im Warenkorb; deine Auswahl bleibt zum erneuten Prüfen erhalten.');return;}
        if(action!=='checkout'){
          sessionStorage.setItem(pendingKey,JSON.stringify({...readPending(),cartState:result.state,expiresAt:result.expiresAt,priceMinor:result.priceMinor}));
          if(result.state==='reserved'){const rows=readMixBasket(),index=rows.findIndex(row=>row.request.id===input.id);if(index>=0)rows[index]=readPending();else rows.push(readPending());writeMixBasket(rows);}
          showMixResume();resumeMixBox?.scrollIntoView?.({block:'center',behavior:'smooth'});return;
        }
        const url=new URL(result.checkoutUrl);
        if(url.protocol!=='https:' || !['snackshark.de','www.snackshark.de','34f5eb-3.myshopify.com'].includes(url.hostname))throw Error('Checkout-Adresse konnte nicht bestätigt werden.');
        // Keep the same request on return from an abandoned checkout.
        window.location.assign(url.href);
      } catch(error){announce(error.message || 'Die Rückmeldung ist unklar. Bitte die begonnene Bestellung fortsetzen, nicht neu anlegen.');}
      finally{clearTimeout(slowTimer);state.submitting=false;root.removeAttribute('aria-busy');if(resumeButton)resumeButton.disabled=false;if(!disposed){showMixResume();updateFooter();}
        if(action==='status'&&readPending()?.request&&readPending()?.cartState==='reserved')mixCartTimer=setTimeout(refreshMixCartTimer,5000);
      }
    };
    showMixResume();
    const addToCart = async () => {
      if (disposed || state.submitting || (studio && (!studioCheckoutEnabled || state.demoMode || state.submitLocked)) || state.step !== 'review' || !validMix()) return;
      const terms = q('[data-native-terms]');
      if (!terms?.checked) {
        if (studio && terms) {
          terms.setAttribute('aria-invalid', 'true');
          terms.closest('.native-config__legal')?.classList.add('is-invalid');
          const error = q('[data-native-terms-error]');
          if (error) { error.textContent = text('terms_required', 'Bitte bestätige den Hinweis zur individuellen Anfertigung, bevor du fortfährst.'); error.hidden = false; }
          terms.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          terms.focus({ preventScroll: true });
        }
        return;
      }
      if(studio && !normalShopifyCart && state.catalog?.checkout?.mode==='erp-reserved')return beginReservedCheckout();
      state.submitting = true;
      root.setAttribute('aria-busy', 'true');
      updateFooter();
      const properties = {
        Candies: selectionText(state.ingredients), Extras: selectionText(state.toppings),
        Verpackung: state.base.packagingLabel || state.packaging,
        '_Configurator Units': '1', '_Configurator Version': 'SnackShark Native v2',
        _Packauftrag: JSON.stringify({
          mode,
          packaging_sku: state.base.sku || '',
          ingredients: selectedList(state.ingredients).map(entry => ({ sku: entry.product._erp?.sku || '', title: entry.product.title, quantity: entry.qty })),
          extras: selectedList(state.toppings).map(entry => ({ sku: entry.product._erp?.sku || '', title: entry.product.title, quantity: entry.qty }))
        })
      };
      const nonce = studio ? (window.crypto?.randomUUID?.() || `mix-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`) : '';
      if (nonce) properties['_Configurator Request'] = nonce;
      let postStarted = false;
      const clearPending = () => { state.pendingNonce = ''; state.pendingConfirmed = false; state.submitLocked = false; try { sessionStorage.removeItem(pendingKey); } catch (error) {} };
      const persistPending = () => {
        state.pendingNonce = nonce;
        try { sessionStorage.setItem(pendingKey, JSON.stringify({ nonce, variantId: String(state.base.variantId), createdAt: Date.now() })); } catch (error) {}
      };
      try {
        state.catalog = null;
        const catalog = await loadCatalog();
        if (studio) await hydrateStudioBaseVariants(catalog);
        applyCatalogToBaseProducts(catalog);
        if (disposed || !validMix()) throw new Error(text('selection_changed', 'Die Verfügbarkeit oder Mix-Regeln haben sich geändert. Bitte prüfe deine Auswahl erneut.'));
        if (studio) persistPending();
        const addPath = root.dataset.cartAddUrl || localPath('/cart/add');
        postStarted = true;
        const response = await request(addPath.endsWith('.js') ? addPath : addPath + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: studio ? String(state.base.variantId) : Number(state.base.variantId), quantity: 1, properties }] })
        });
        if (!response.ok) {
          if (studio && [400, 422].includes(response.status)) { postStarted = false; clearPending(); }
          throw new Error(text('cart_failed', 'Der Mix konnte nicht hinzugefügt werden. Bitte prüfe deine Auswahl und versuche es erneut.'));
        }
        if (studio) state.pendingConfirmed = true;
        try {
          sessionStorage.setItem('snacksharkLastMix', JSON.stringify({
            mode, path: window.location.pathname, packaging: state.packaging, base: state.base,
            ingredients: selectedList(state.ingredients).map(entry => ({ id: String(entry.product.id), qty: entry.qty })),
            toppings: selectedList(state.toppings).map(entry => ({ id: String(entry.product.id), qty: entry.qty }))
          }));
        } catch (error) {}
        if (disposed) return;
        if(normalShopifyCart)clearPending();
        const destination = new URL(normalShopifyCart ? localPath('/') : root.dataset.cartUrl || localPath('/cart'), window.location.origin);
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) destination.searchParams.set('preview_theme_id', preview);
        window.location.assign(destination.pathname + destination.search + destination.hash);
      } catch (error) {
        state.submitting = false;
        root.removeAttribute('aria-busy');
        if (studio && postStarted) {
          state.submitLocked = true;
          persistPending();
          // A lost response can follow a successful POST. A GET can prove success,
          // but an empty read cannot prove that the earlier POST will never commit.
          try {
            const response = await request('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
            const cart = response.ok ? await response.json() : null;
            if (cart?.items?.some(item => item.properties?.['_Configurator Request'] === nonce && String(item.variant_id || item.id) === String(state.base?.variantId))) {
              state.pendingConfirmed = true;
              state.submitting = true;
              if(normalShopifyCart)clearPending();
              const destination = new URL(normalShopifyCart ? localPath('/') : root.dataset.cartUrl || localPath('/cart'), window.location.origin);
              const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
              if (preview) destination.searchParams.set('preview_theme_id', preview);
              window.location.assign(destination.pathname + destination.search);
              return;
            }
          } catch (readError) { state.submitting = false; }
          announce(text('studio_cart_uncertain', 'Die Rückmeldung ist unklar. Bitte prüfe deinen Warenkorb, bevor du erneut bestellst. Dieser Mix bleibt vorsichtshalber gesperrt.'));
          if (!disposed) updateFooter();
          return;
        }
        const changed = text('selection_changed', 'Die Verfügbarkeit oder Mix-Regeln haben sich geändert. Bitte prüfe deine Auswahl erneut.');
        announce(error.message === changed ? changed : text('cart_failed', 'Der Mix konnte nicht hinzugefügt werden. Bitte prüfe deine Auswahl und versuche es erneut.'));
        if (!disposed) updateFooter();
      }
    };

    const advance = () => {
      if (disposed || state.submitting || (studio && state.studioLoading) || nextButtons[0]?.disabled) return;
      if (state.step === 'packaging') { filterBaseProducts(); setStep('size'); }
      else if (state.step === 'size') { ensureProducts('ingredients'); setStep('ingredients'); }
      else if (state.step === 'ingredients') {
        if (mode === 'muesli') { ensureProducts('toppings'); setStep('toppings'); }
        else { showReview(); setStep('review'); }
      } else if (state.step === 'toppings') { showReview(); setStep('review'); }
      else addToCart();
    };

    const resetFlow = () => {
      state.flowRevision += 1;
      setStudioIngredientLoading(false);
      state.step = 'packaging'; state.packaging = ''; state.base = null;
      state.ingredients.clear(); state.toppings.clear(); state.upsellsLoaded = false;
      qa('.is-selected').forEach(node => node.classList.remove('is-selected'));
      resetDiscoveryFilters();
      const upsells = q('[data-native-upsells]');
      if (upsells) upsells.hidden = true;
      qa('[data-base-product]').forEach(card => { card.hidden = false; });
      updateMeter('ingredients'); updateMeter('toppings'); setStep('packaging');
    };

    const exitNow = () => {
      const destination = new URL(localPath('/'), window.location.origin);
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) destination.searchParams.set('preview_theme_id', preview);
      window.location.assign(destination.pathname + destination.search);
    };
    const askToExit = () => {
      if (!hasProgress()) { exitNow(); return; }
      const dialog = q('[data-native-exit-confirm]');
      if (dialog) { dialog.hidden = false; dialog.setAttribute('aria-hidden', 'false'); }
    };

    nextButtons.forEach(button => on(button, 'click', event => { event.stopPropagation(); advance(); }));
    backButtons.forEach(button => on(button, 'click', event => {
      if (state.submitting) return;
      event.stopPropagation();
      const index = steps.indexOf(state.step);
      if (index > 0) { state.flowRevision += 1; setStudioIngredientLoading(false); setStep(steps[index - 1]); }
    }));

    on(root, 'click', async event => {
      if (studio && state.studioLoading && event.target.closest('[data-studio-filter-summary]')) { event.preventDefault(); return; }
      if (disposed || state.submitting || event.target.closest('button:disabled, [aria-disabled="true"]')) return;
      const slotButton = event.target.closest('[data-studio-slot]');
      if (slotButton) {
        if (studioReplacementIntent) chooseStudioReplacementTarget(Number(slotButton.dataset.studioSlot));
        else openStudioSlot(Number(slotButton.dataset.studioSlot));
        return;
      }
      const slotAdd = event.target.closest('[data-studio-slot-add]');
      if (slotAdd) { duplicateStudioSlot(Number(slotAdd.dataset.studioSlotAdd), slotAdd); return; }
      if (event.target.closest('[data-studio-replace-cancel]')) { cancelStudioReplacement(); return; }
      if (event.target.closest('[data-studio-random-create]')) { createStudioRandom(); return; }
      if (event.target.closest('[data-studio-upgrade-replace]')) {
        const intent = studioDuplicateIntent, dialog = q('[data-studio-upgrade]');
        if (!intent || !dialog?.open || dialog.getAttribute('aria-busy') === 'true' || !studioTwoViewActive() || !validBase()
          || intent.base !== state.base || intent.revision !== state.flowRevision || !state.ingredients.has(intent.productId)) return;
        closeStudioDialog(false); studioReplacementIntent = intent;
        returnStudioCreation(false); renderStudioSlots(); focusStudioSlot(0);
        announce(format('studio_replace_choose', 'Wähle eine Portion, die durch {title} ersetzt werden soll.', { title: state.ingredients.get(intent.productId).product.title }));
        return;
      }
      if (event.target.closest('[data-studio-picker-close]')) { returnStudioCreation(); return; }
      const slotRemove = event.target.closest('[data-studio-slot-remove]');
      if (slotRemove) {
        if (!studioTwoViewActive() || state.studioView !== 'creation' || state.studioLoading || studioReplacementIntent) return;
        const index = Number(slotRemove.dataset.studioSlotRemove), id = state.studioSlots[index], entry = state.ingredients.get(id);
        if (!Number.isInteger(index) || !entry) return;
        if (entry.qty === 1) state.ingredients.delete(id); else state.ingredients.set(id, { ...entry, qty: entry.qty - 1 });
        state.studioSlots[index] = null;
        updateMeter('ingredients'); updateFooter(); focusStudioSlot(index); return;
      }
      if (studio && event.target.closest('[data-studio-zoom-open]')) { openStudioZoom(event.target.closest('[data-studio-zoom-open]')); return; }
      if (studio && event.target.closest('[data-studio-dialog-close]')) { closeStudioDialog(); return; }
      if (studio && event.target.closest('[data-studio-upgrade-change]')) { closeStudioDialog(); setDrawer(q('[data-native-advisor]'), false); state.flowRevision += 1; setStudioIngredientLoading(false); setStep('packaging'); return; }
      const upgradeChoice = studio && event.target.closest('[data-studio-upgrade-variant]');
      if (upgradeChoice) { await chooseStudioUpgrade(upgradeChoice.dataset.studioUpgradeVariant); return; }
      const studioFilter = studio && event.target.closest('[data-studio-filter]');
      if (studioFilter) {
        state.studioFilter = ['', 'fruchtig', 'sauer', 'schoko', 'vegan', 'halal'].includes(studioFilter.dataset.studioFilter) ? studioFilter.dataset.studioFilter : '';
        updateStudioFilterMenu();
        applySearch(q('[data-ingredient-search]'), 'ingredients');
        closeStudioFilterMenu(true);
        return;
      }
      if (studio && event.target.closest('[data-studio-change-package]')) { state.flowRevision += 1; setStudioIngredientLoading(false); setStep('packaging'); return; }
      const demoCard = studio && state.demoMode && event.target.closest('[data-demo-package]');
      if (demoCard) {
        const descriptor = demoPackages.find(item => item.id === demoCard.dataset.demoPackage && packageGroup(item) === state.packaging);
        if (!descriptor || !demoReady()) return;
        const selectionRevision = ++state.flowRevision;
        state.base = { demo: true, title: descriptor.title, price: null, productId: '', variantId: '', sku: '', packaging: state.packaging, packagingLabel: descriptor.title, pickCount: Number(descriptor.capacity), toppingCount: 0, _preview: descriptor };
        state.ingredients.clear(); state.toppings.clear(); state.products = []; state.upsellsLoaded = false;
        resetDiscoveryFilters();
        qa('[data-demo-package]').forEach(card => card.classList.toggle('is-selected', card === demoCard));
        updateCandyPreview();
        setNext(text('loading_label', 'Sortiment wird geladen …'), true);
        await ensureProducts('ingredients');
        if (disposed || selectionRevision !== state.flowRevision || !validBase()) return;
        setStep('ingredients');
        announce(text('studio_browse_note', 'Du erkundest echte ERP-Sorten. Für diese Vorschau sind Bestellen und Preise deaktiviert.'));
        return;
      }
      const packagePreview = event.target.closest('[data-package-preview]');
      if (packagePreview) {
        const body = q('[data-native-info-body]');
        const previewImage = safeUrl(packagePreview.dataset.previewImage);
        if (body) body.innerHTML = `<p class="eyebrow">${label('package_info_title', 'Echte Verpackung')}</p><h2 id="NativeInfoTitle-${escapeHtml(root.dataset.sectionId)}">${escapeHtml(packagePreview.dataset.previewTitle || text('package_preview_title', 'So sieht deine Verpackung aus'))}</h2>${previewImage ? `<img class="native-info__image native-info__image--package" src="${escapeHtml(previewImage)}" alt="${escapeHtml(packagePreview.dataset.previewTitle || text('package_info_title', 'Echte Verpackung'))}">` : ''}<p>${label('package_preview_description', 'Das Foto zeigt die aktuell hinterlegte Ausführung. Das Verpackungsdesign kann abweichen.')}</p>`;
        setDrawer(q('[data-native-info]'), true);
        return;
      }

      const packaging = event.target.closest('button.native-pack-card[data-packaging]');
      if (packaging) {
        state.flowRevision += 1;
        setStudioIngredientLoading(false);
        resetDiscoveryFilters();
        state.packaging = packaging.dataset.packaging;
        qa('button.native-pack-card[data-packaging]').forEach(card => card.classList.toggle('is-selected', card === packaging));
        state.base = null; state.ingredients.clear(); state.toppings.clear();
        if (studio) state.products = [];
        filterBaseProducts(); setStep('size');
        announce(text('packaging_chosen', 'Verpackung gewählt. Jetzt fehlt nur noch die Größe.'));
        return;
      }

      const baseCard = event.target.closest('[data-base-product]');
      if (baseCard && !state.demoMode && state.catalog && !state.catalogError && !baseCard.disabled && !baseCard.classList.contains('is-soldout')) {
        const selectionRevision = ++state.flowRevision;
        const labels = { tuete: mode === 'muesli' ? text('pack_muesli_title', 'Müsli-Tüte') : text('pack_bag_title', 'Wunschtüte'), flasche: text('pack_bottle_title', 'Wunschflasche'), sonderform: text('pack_special_title', 'Bucket & Sonderform'), buchstaben_zahlen: text('pack_letters_title', 'Buchstaben & Zahlen') };
        state.base = {
          productId: baseCard.dataset.productId, variantId: baseCard.dataset.variantId,
          title: baseCard.dataset.title, price: Number(baseCard.dataset.price),
          pickCount: Number(baseCard.dataset.pickCount), toppingCount: Number(baseCard.dataset.toppingCount),
          packaging: baseCard.dataset.packaging, packagingLabel: labels[baseCard.dataset.packaging] || baseCard.dataset.packaging,
          sku: baseCard.dataset.erpSku || baseCard.dataset.sku || ''
        };
        if (studio) {
          const details = studioBaseItems.get(baseCard);
          const previews = Array.isArray(previewConfig.packages) ? previewConfig.packages : [];
          state.base.portionGrams = Number(baseCard.dataset.portionGrams);
          state.base._erp = details?.item;
          state.base._profile = details?.profile;
          state.base._preview = previews.find(item => item && item.sku === state.base.sku) || { id: state.base.sku, ...previewDescriptor() };
          state.products = [];
        }
        state.ingredients.clear(); state.toppings.clear(); state.upsellsLoaded = false;
        if (studio) resetDiscoveryFilters();
        qa('[data-base-product]').forEach(card => card.classList.toggle('is-selected', card === baseCard));
        setNext(text('loading_label', 'Sortiment wird geladen …'), true);
        await ensureProducts('ingredients');
        if (disposed || selectionRevision !== state.flowRevision || !validBase()) return;
        setStep('ingredients');
        announce(format('size_chosen', '{title} gewählt. Jetzt stellst du deinen Mix zusammen.', { title: state.base.title }));
        return;
      }

      const ingredientCard = event.target.closest('[data-ingredient-card]');
      if (ingredientCard && (event.target.closest('[data-card-plus]') || event.target.closest('[data-card-minus]'))) {
        if (studio && state.studioLoading) { announce(text('loading_label', 'Sortiment wird geladen …')); return; }
        const isTopping = ingredientCard.dataset.kind === 'toppings';
        const map = isTopping ? state.toppings : state.ingredients;
        const products = isTopping ? state.toppingProducts : state.products;
        const product = products.find(item => String(item.id) === ingredientCard.dataset.productId);
        if (!product || !state.base || (!studio && !product.variants?.some(variant => variant.available))) return;
        if (!isTopping && studioTwoViewActive()) {
          if (state.studioView === 'picker' && event.target.closest('[data-card-plus]')) chooseStudioSlotProduct(product);
          return;
        }
        const limit = Number(isTopping ? state.base?.toppingCount : state.base?.pickCount) || 0;
        const total = selectedTotal(map);
        const current = map.get(ingredientCard.dataset.productId) || { product, qty: 0 };
        if (studio && !isTopping && event.target.closest('[data-card-plus]') && limit > 0 && total >= limit) { openStudioUpgrade(event.target.closest('[data-card-plus]')); return; }
        if (event.target.closest('[data-card-plus]') && total < limit && hasPortions(product, current.qty + 1)) current.qty += 1;
        if (event.target.closest('[data-card-minus]')) current.qty = Math.max(0, current.qty - 1);
        if (current.qty) map.set(ingredientCard.dataset.productId, current); else map.delete(ingredientCard.dataset.productId);
        if (!isTopping && current.qty && state.lastAdvisorQuery) recordAffinity(state.lastAdvisorQuery, product.id);
        updateMeter(isTopping ? 'toppings' : 'ingredients'); updateFooter();
        return;
      }

      if (ingredientCard && event.target.closest('[data-card-info]')) {
        const products = ingredientCard.dataset.kind === 'toppings' ? state.toppingProducts : state.products;
        const product = products.find(item => String(item.id) === ingredientCard.dataset.productId);
        const body = q('[data-native-info-body]');
        if (body && product) body.innerHTML = `<p class="eyebrow">${label('product_info_title', 'Produktinformationen')}</p><h2 id="NativeInfoTitle-${escapeHtml(root.dataset.sectionId)}">${escapeHtml(product.title)}</h2>${productImage(product) ? `<img class="native-info__image" src="${escapeHtml(productImage(product))}" alt="${escapeHtml(product.title)}">` : ''}<p class="native-info__availability">${studio && state.demoMode ? label('studio_preview_only', 'Vorschau – noch nicht bestellbar') : product.variants?.some(variant => variant.available) ? label('available', 'Verfügbar') : label('sold_out', 'Aktuell nicht verfügbar')}</p><div class="native-info__tags">${productTags(product).map(tag => `<span>${escapeHtml(tag)}</span>`).join('')}</div><div class="native-info__content">${product._erp?.role === 'candy' ? renderCandyInformation(product) : cleanHtml(product.body_html) || `<p>${label('information_missing', 'Für diese Sorte sind noch keine ausführlichen Zutaten- und Nährwertangaben hinterlegt.')}</p>`}</div>`;
        setDrawer(q('[data-native-info]'), true);
        return;
      }

      if (event.target.closest('[data-native-info-close]')) setDrawer(q('[data-native-info]'), false);
      if (event.target.closest('[data-native-advisor-open]')) setDrawer(q('[data-native-advisor]'), true);
      if (event.target.closest('[data-native-advisor-close]')) setDrawer(q('[data-native-advisor]'), false);
      const advisorChoice = event.target.closest('[data-advisor-term]');
      if (advisorChoice) advisorChoice.classList.toggle('is-selected');

      if (event.target.closest('[data-native-advisor-apply]')) {
        state.advisorTerms = qa('[data-advisor-term].is-selected').map(button => button.dataset.advisorTerm);
        const search = q('[data-ingredient-search]');
        if (search) search.value = state.advisorTerms.join(' ');
        setDrawer(q('[data-native-advisor]'), false);
        if (state.step !== 'ingredients') { await ensureProducts('ingredients'); setStep('ingredients'); }
        applySearch(search, 'ingredients', state.advisorTerms);
        announce(state.advisorTerms.length ? text('filters_active', 'Filter aktiv – das Regal zeigt passende Sorten.') : text('filters_removed', 'Alle Filter wurden entfernt.'));
      }

      if (event.target.closest('[data-advisor-recommend]')) {
        await ensureProducts('ingredients');
        const input = q('[data-advisor-favorite]');
        const result = q('[data-advisor-results]');
        const favorite = String(input?.value || '').trim().toLowerCase();
        if (!result) return;
        if (favorite.length < 2) { result.innerHTML = `<p>${mode === 'muesli' ? label('advisor_query_muesli', 'Nenne kurz dein Lieblings-Müsli, eine Zutat oder eine Geschmacksrichtung – dann legen wir los.') : label('advisor_query_candy', 'Schreib kurz deine Lieblingssorte hinein – dann legen wir los.')}</p>`; return; }
        state.lastAdvisorQuery = favorite;
        const synonymGroups = mode === 'muesli' ? [
          [/schoko|choco|kakao/, ['schoko', 'choco', 'kakao', 'cookie']],
          [/erdbeer|strawberry|beere|berry/, ['erdbeer', 'strawberry', 'beere', 'berry', 'frucht']],
          [/nuss|nut|mandel|haselnuss/, ['nuss', 'nut', 'mandel', 'haselnuss']],
          [/crunch|knusper|knusprig/, ['crunch', 'crunchy', 'knusper', 'knusprig']],
          [/banane|banana/, ['banane', 'banana']], [/protein|fitness/, ['protein', 'fitness']]
        ] : [
          [/goldbär|goldbaer|gummibär|haribo/, ['bär', 'bear', 'frucht', 'gummi', 'monkey', 'affen', 'alpaka', 'schlangen', 'herzen', 'ringe']],
          [/pico|balla|gefüllt|gefuellt/, ['pico', 'balla', 'gefüllt', 'filled']],
          [/cola/, ['cola', 'kola']], [/schlumpf|schlümpf|blau|blue/, ['schlumpf', 'blau', 'blue', 'pixel', 'stern']],
          [/sauer|sour/, ['sauer', 'sour']], [/lakritz|salzig/, ['lakritz', 'salz']],
          [/erdbeer|strawberry/, ['erdbeer', 'strawberry']], [/tropisch|ananas|pineapple/, ['tropical', 'ananas', 'pineapple']]
        ];
        const terms = [...favorite.split(/\s+/), ...synonymGroups.filter(([test]) => test.test(favorite)).flatMap(([, words]) => words)];
        const affinity = readAffinity()[favorite] || {};
        const requiredDietary = terms.filter(term => dietaryFieldFor(term));
        const matches = state.products.filter(product => (studio ? hasPortions(product, 1) : product.variants?.some(variant => variant.available)) && requiredDietary.every(term => matchesTerm(product, term)))
          .map(product => ({ product, score: terms.reduce((score, term) => score + (matchesTerm(product, term) ? 1 : 0), 0) + Number(affinity[String(product.id)] || 0) * 2 }))
          .filter(item => item.score > 0).sort((a, b) => b.score - a.score).slice(0, 6);
        result.innerHTML = matches.length ? `<p><strong>${label('advisor_results_heading', 'Das könnte deine nächste Lieblingssorte werden:')}</strong></p><div class="native-advisor__result-grid">${matches.map(({ product }) => {
          const image = productImage(product);
          return `<article>${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : `<span>${studio ? label('studio_no_image', 'Noch kein Produktfoto') : mode === 'muesli' ? '🥣' : '🍬'}</span>`}<strong>${escapeHtml(product.title)}</strong><button type="button" data-advisor-result-add data-product-id="${escapeHtml(product.id)}">${label('add_to_mix', 'Zum Mix +')}</button></article>`;
        }).join('')}</div>` : `<p>${mode === 'muesli' ? label('advisor_empty_muesli', 'Keine passende Sorte gefunden. Versuche eine Zutat oder Richtung wie „Schoko“, „Erdbeere“ oder „knusprig“.') : label('advisor_empty_candy', 'Keine passende Sorte gefunden. Versuche einen Geschmack oder eine Form, zum Beispiel „sauer“, „Cola“ oder „gefüllt“.')}</p>`;
      }

      const advisorAdd = event.target.closest('[data-advisor-result-add]');
      if (advisorAdd) {
        const product = state.products.find(item => String(item.id) === advisorAdd.dataset.productId);
        if (studioTwoViewActive()) {
          if (state.studioView === 'picker' && studioSlotCandidate(product)) {
            setDrawer(q('[data-native-advisor]'), false);
            chooseStudioSlotProduct(product);
          }
          return;
        }
        const limit = Number(state.base?.pickCount || 0);
        if (studio && product && limit > 0 && selectedTotal(state.ingredients) >= limit) { openStudioUpgrade(advisorAdd); return; }
        if (hasPortions(product, Number(state.ingredients.get(String(product?.id))?.qty || 0) + 1) && selectedTotal(state.ingredients) < limit) {
          const key = String(product.id);
          const current = state.ingredients.get(key) || { product, qty: 0 };
          current.qty += 1; state.ingredients.set(key, current);
          recordAffinity(state.lastAdvisorQuery, product.id);
          updateMeter('ingredients'); updateFooter();
          advisorAdd.textContent = text('in_mix', '✓ Im Mix'); advisorAdd.disabled = true;
        }
      }

      if (event.target.closest('[data-native-surprise]') || event.target.closest('[data-studio-random]')) {
        if (studio) { openStudioRandom(event.target.closest('[data-native-surprise]') || event.target.closest('[data-studio-random]')); return; }
        const limit = Number(state.base?.pickCount || 0);
        const available = state.products.filter(product => product.variants?.some(variant => variant.available)).slice().sort(() => Math.random() - .5).slice(0, limit);
        state.ingredients.clear();
        available.forEach(product => state.ingredients.set(String(product.id), { product, qty: 1 }));
        updateMeter('ingredients'); updateFooter();
        announce(text('surprise_selected', 'Überraschungsmix ausgewählt – unten siehst du jede Sorte und kannst sie direkt entfernen.'));
      }

      const remove = event.target.closest('[data-selection-remove]');
      if (remove) {
        const map = remove.dataset.kind === 'toppings' ? state.toppings : state.ingredients;
        map.delete(remove.dataset.productId);
        updateMeter(remove.dataset.kind); updateFooter();
      }
      const adjust = event.target.closest('[data-selection-adjust]');
      if (adjust) {
        const map = adjust.dataset.kind === 'toppings' ? state.toppings : state.ingredients;
        const entry = map.get(adjust.dataset.productId);
        const limit = Number(adjust.dataset.kind === 'toppings' ? state.base?.toppingCount : state.base?.pickCount) || 0;
        const delta = Number(adjust.dataset.delta || 0);
        if (studio && adjust.dataset.kind !== 'toppings' && entry && delta > 0 && limit > 0 && selectedTotal(map) >= limit) { openStudioUpgrade(adjust); return; }
        if (entry && (delta < 0 || (hasPortions(entry.product, entry.qty + 1) && selectedTotal(map) < limit))) {
          entry.qty = Math.max(0, entry.qty + delta);
          if (entry.qty) map.set(adjust.dataset.productId, entry); else map.delete(adjust.dataset.productId);
          updateMeter(adjust.dataset.kind); updateFooter();
        }
      }
      if (event.target.closest('[data-selection-clear]') || event.target.closest('[data-studio-clear]')) {
        if (studioReplacementIntent) return;
        const kind = state.step === 'toppings' ? 'toppings' : 'ingredients';
        const map = kind === 'toppings' ? state.toppings : state.ingredients;
        map.clear();
        if (studioTwoViewActive()) returnStudioCreation(false);
        updateMeter(kind); updateFooter(); announce(text('selection_cleared', 'Auswahl geleert.'));
        if (studioTwoViewActive()) focusStudioSlot(0);
      }
      const dockToggle = event.target.closest('[data-selection-dock-toggle]');
      if (dockToggle) {
        const dock = q('[data-selection-dock]');
        const expanded = dock?.classList.toggle('is-expanded');
        dockToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      const upsell = event.target.closest('[data-upsell-add]');
      if (upsell && !upsell.disabled && !studio) {
        upsell.disabled = true; upsell.textContent = text('upsell_adding', 'Wird hinzugefügt …');
        try {
          const response = await request('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(upsell.dataset.variantId), quantity: 1 }] })
          });
          if (!response.ok) throw new Error(text('sold_out', 'Aktuell nicht verfügbar'));
          upsell.textContent = text('in_cart', '✓ Im Warenkorb');
          // This POST has succeeded. Rendering failures must not re-enable an add
          // button or send the same addition again.
          try {
            const cartData = await response.json().catch(() => ({}));
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'native-configurator', cartData });
            } else {
              await document.querySelector('cart-drawer')?.refresh?.(false);
            }
          } catch (error) {
            announce(text('cart_refresh_failed', 'Hinzugefügt. Bitte lade die Seite neu, um den aktuellen Warenkorb zu sehen.'));
          }
        } catch (error) { upsell.disabled = false; upsell.textContent = text('upsell_add', '+ Dazu'); announce(text('upsell_failed', 'Das Zusatzprodukt konnte gerade nicht hinzugefügt werden.')); }
      }

      const progressButton = event.target.closest('[data-progress-step]');
      if (progressButton && !progressButton.disabled && steps.indexOf(progressButton.dataset.progressStep) < steps.indexOf(state.step)) { state.flowRevision += 1; setStep(progressButton.dataset.progressStep); }
      if (event.target.closest('[data-native-exit]')) askToExit();
      if (event.target.closest('[data-native-exit-cancel]')) {
        const dialog = q('[data-native-exit-confirm]');
        if (dialog) { dialog.hidden = true; dialog.setAttribute('aria-hidden', 'true'); }
      }
      if (event.target.closest('[data-native-exit-discard]')) {
        const dialog = q('[data-native-exit-confirm]');
        if (dialog) { dialog.hidden = true; dialog.setAttribute('aria-hidden', 'true'); }
        resetFlow(); exitNow();
      }
    });

    on(q('[data-ingredient-search]'), 'input', event => applySearch(event.target, 'ingredients'));
    on(q('[data-topping-search]'), 'input', event => applySearch(event.target, 'toppings'));
    on(q('[data-native-terms]'), 'change', () => { clearTermsError(); updateFooter(); });
    on(root, 'change', event => {
      const preference = event.target.closest('[data-studio-random-preference]');
      if (!studio || !preference || !q('[data-studio-random-dialog]')?.open || !studioFilterValues.includes(preference.value)) return;
      studioRandomPreference = preference.value; updateStudioRandom();
    });
    if (studioMobileQuery) {
      const onMobileChange = () => {
        if (disposed) return;
        const focus = document.activeElement;
        const wasTwoView = !!root.dataset.studioMobileView;
        if (root.dataset.studioMobileView === 'picker') studioPickerScroll = q('.native-config__workspace')?.scrollTop || 0;
        studioReplacementIntent = null;
        studioViewRevision += 1; state.studioView = 'creation'; state.studioTargetSlot = null; studioSlotIntent = null;
        updateFooter(); syncStudioMobileLayout(); renderSelectionDock();
        if (studioTwoViewMarkup && state.step === 'ingredients' && focus && root.contains(focus) && !studioDialogs().some(dialog => dialog.open)) {
          if (studioTwoViewActive()) focusStudioSlot(0);
          else if (wasTwoView) q('[data-ingredient-search]')?.focus({ preventScroll: true });
        }
      };
      if (typeof studioMobileQuery.addEventListener === 'function') on(studioMobileQuery, 'change', onMobileChange);
      else if (typeof studioMobileQuery.addListener === 'function') {
        studioMobileQuery.addListener(onMobileChange);
        const cleanupMobile = studioMobileCleanup;
        studioMobileCleanup = () => { studioMobileQuery.removeListener(onMobileChange); cleanupMobile?.(); };
      }
    }

    const enterFlow = () => {
      if (disposed) return;
      document.body.classList.add('native-config-flow-active');
      root.classList.add('is-running');
      setStep(state.step);
    };
    const restoreLastMix = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('resume_mix') !== '1') return;
      params.delete('resume_mix');
      history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`);
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem('snacksharkLastMix') || 'null'); } catch (error) { saved = null; }
      if (!saved || saved.mode !== mode || !saved.base) { announce(text('last_mix_missing', 'Der letzte Mix ist nicht mehr verfügbar. Starte bitte neu.')); return; }
      const restoreRevision = ++state.flowRevision;
      await loadCatalog();
      if (disposed || restoreRevision !== state.flowRevision) return;
      state.packaging = saved.packaging || saved.base.packaging;
      const savedCard = baseCardFor(saved.base);
      if (!savedCard || savedCard.disabled) { announce(text('last_packaging_missing', 'Die Verpackung deines letzten Mix ist nicht mehr verfügbar.')); return; }
      state.base = { ...saved.base, price: Number(savedCard.dataset.price), pickCount: Number(savedCard.dataset.pickCount), toppingCount: Number(savedCard.dataset.toppingCount), sku: savedCard.dataset.erpSku || savedCard.dataset.sku || '' };
      if (studio) {
        if (state.demoMode || saved.base.demo) { state.base = null; return; }
        const details = studioBaseItems.get(savedCard);
        state.base.portionGrams = Number(savedCard.dataset.portionGrams);
        state.base._erp = details?.item; state.base._profile = details?.profile;
        state.base._preview = (Array.isArray(previewConfig.packages) ? previewConfig.packages : []).find(item => item?.sku === state.base.sku) || { id: state.base.sku, shape: 'bag' };
      }
      filterBaseProducts();
      qa('button.native-pack-card[data-packaging]').forEach(card => card.classList.toggle('is-selected', card.dataset.packaging === state.packaging));
      qa('[data-base-product]').forEach(card => card.classList.toggle('is-selected', studio ? String(card.dataset.variantId) === String(state.base.variantId) : String(card.dataset.productId) === String(state.base.productId)));
      enterFlow();
      await ensureProducts('ingredients');
      if (disposed || restoreRevision !== state.flowRevision) return;
      state.ingredients.clear();
      (saved.ingredients || []).forEach(savedEntry => {
        const product = state.products.find(item => String(item.id) === String(savedEntry.id));
        if ((studio ? hasPortions(product, savedEntry.qty) && selectedTotal(state.ingredients) + savedEntry.qty <= state.base.pickCount : product?.variants?.some(variant => variant.available)) && Number.isInteger(savedEntry.qty) && savedEntry.qty > 0) state.ingredients.set(String(product.id), { product, qty: Number(savedEntry.qty) || 1 });
      });
      if (mode === 'muesli') {
        await ensureProducts('toppings');
        if (disposed || restoreRevision !== state.flowRevision) return;
        state.toppings.clear();
        (saved.toppings || []).forEach(savedEntry => {
          const product = state.toppingProducts.find(item => String(item.id) === String(savedEntry.id));
          if (product?.variants?.some(variant => variant.available) && Number.isInteger(savedEntry.qty) && savedEntry.qty > 0) state.toppings.set(String(product.id), { product, qty: Number(savedEntry.qty) || 1 });
        });
      }
      setStep('ingredients');
      updateMeter('ingredients');
      announce(text('last_mix_restored', 'Dein letzter Mix ist wieder da. Du kannst jede Sorte direkt ändern.'));
    };
    document.querySelectorAll('a[href="#SnackSharkConfigurator"]').forEach(link => on(link, 'click', event => {
      event.preventDefault(); enterFlow();
    }));
    updateProgress(); updateFooter(); renderSelectionDock();
    loadCatalog().then(async catalog => {
      if (disposed) return;
      if (studio) await hydrateStudioBaseVariants(catalog);
      if (disposed) return;
      applyCatalogToBaseProducts(catalog);
      if (studio && (!studioCheckoutEnabled || (root.dataset.studioDemo === 'true' && !qa('[data-base-product]').some(card => !card.disabled)))) {
        state.demoMode = true;
        root.dataset.studioDemoMode = 'true';
        await loadBrowseCatalog();
        if (disposed) return;
        renderDemoPackages();
        qa('button.native-pack-card[data-packaging]').forEach(button => { button.disabled = !demoReady() || !demoPackages.some(item => packageGroup(item) === button.dataset.packaging); });
      }
      if (studio) {
        root.dataset.studioDemoMode = String(state.demoMode);
        const note = q('[data-studio-browse-note]');
        if (note) { note.hidden = !state.demoMode; note.textContent = text('studio_browse_note', 'Du erkundest echte ERP-Sorten. Für diese Vorschau sind Bestellen und Preise deaktiviert.'); }
      }
      filterBaseProducts();
      updateFooter();
      announce(state.demoMode ? text('studio_browse_note', 'Du erkundest echte ERP-Sorten. Für diese Vorschau sind Bestellen und Preise deaktiviert.') : state.inventoryReady
        ? text('inventory_ready', 'Dein Mix kann losgehen. Verpackungen und Zutaten sind verfügbar.')
        : text('empty_catalog_text', 'Der Konfigurator wird gerade vorbereitet. Aktuell ist kein vollständiges Sortiment für deinen Mix verfügbar. Bitte schau später wieder vorbei.'));
      if (studio && !state.demoMode) await restoreLastMix().catch(() => announce(text('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.')));
    }).catch(error => {
      state.inventoryReady = false;
      qa('button.native-pack-card[data-packaging]').forEach(button => { button.disabled = true; });
      announce(text('configurator_unreachable', 'Der Konfigurator ist gerade nicht erreichbar. Bitte lade die Seite neu oder versuche es später noch einmal.'));
      qa('[data-base-product]').forEach(card => {
        card.disabled = true;
        card.classList.add('is-soldout');
        card.setAttribute('aria-disabled', 'true');
      });
    });
    if (studio) { enterFlow(); announce(text('loading_label', 'Sortiment wird geladen …')); }
    else restoreLastMix().catch(() => announce(text('catalog_failed', 'Das Sortiment konnte gerade nicht geladen werden.')));
    on(root, 'keydown', event => {
      // A native top-layer dialog owns its own focus loop, including when it
      // was opened above the existing Mix-Butler drawer.
      if (studio && studioDialogs().some(dialog => dialog.open)) {
        if (event.key === 'Escape') { event.preventDefault(); closeStudioDialog(); }
        return;
      }
      if (event.key === 'Tab') {
        const dialog = qa('[data-native-info], [data-native-advisor]').find(drawer => !drawer.hidden);
        if (dialog) {
          const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), summary, [tabindex="0"]')).filter(node => !node.hidden);
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (first && event.shiftKey && (document.activeElement === first || document.activeElement === dialog.querySelector('aside'))) { event.preventDefault(); last.focus(); }
          else if (last && !event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      }
      if (event.key !== 'Escape' || state.submitting) return;
      if (studioTwoViewActive()) {
        const exit = q('[data-native-exit-confirm]');
        if (exit && !exit.hidden) {
          event.preventDefault(); exit.hidden = true; exit.setAttribute('aria-hidden', 'true');
          q('[data-native-exit]')?.focus({ preventScroll: true }); return;
        }
        if (studioReplacementIntent) { event.preventDefault(); cancelStudioReplacement(); return; }
      }
      if (studioTwoViewActive() && state.studioView === 'picker') {
        const drawer = [q('[data-native-info]'), q('[data-native-advisor]')].find(node => node && !node.hidden);
        if (drawer) { event.preventDefault(); setDrawer(drawer, false); return; }
        event.preventDefault(); returnStudioCreation(); return;
      }
      if (studio && event.target.closest('[data-studio-filter-menu]') && q('[data-studio-filter-menu]')?.open) {
        event.preventDefault();
        closeStudioFilterMenu(true);
        return;
      }
      qa('[data-native-info], [data-native-advisor]').forEach(drawer => setDrawer(drawer, false));
      const dialog = q('[data-native-exit-confirm]');
      if (dialog) { dialog.hidden = true; dialog.setAttribute('aria-hidden', 'true'); }
    });
  };
  const mount = (scope = document) => {
    if (scope.matches?.('[data-native-configurator]')) mountRoot(scope);
    scope.querySelectorAll?.('[data-native-configurator]').forEach(mountRoot);
  };
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    Array.from(instances).forEach(([root, cleanup]) => { if (root === event.target || event.target.contains(root)) cleanup(); });
  });
  window.SnackSharkNativeConfigurator = { mount };
  mount(document);
})();
